import type { Group, Material, Object3D } from 'three';
import { Box3, Mesh, Vector3 } from 'three';

import { getQualitySettings, type QualitySettings } from '../../hooks/use-three-kitchen/quality';
import { AnimationManager } from '../../three-helpers/animation';
import { prepareMaterialsForFade, setObjectOpacity } from '../../three-helpers/animation/transition-helpers/material-state';
import { HighlightManager } from '../../three-helpers/highlight';
import { LightHelperManager } from '../../three-helpers/light';
import { CameraHelperManager } from '../../three-helpers/camera';
import { applyEmissiveTint, EmissiveTintBackup, restoreEmissiveTint } from '../../three-helpers/tint';
import { isHoverGlowObjectType, SCENE_CATEGORY_TYPES, type SceneCategory } from '../../interaction';
import { VENT_CUTOUT_TYPE } from '../managers/cutout-manager/helpers/vent-proxy.helpers';

import {
	BrightnessControl,
	CameraManager,
	CutoutManager,
	DebugManager,
	EventManager,
	ExportManager,
	GeoLightingManager,
	LightingManager,
	ModelManager,
	RotationGizmoManager,
	SceneDiffManager,
	SceneManager,
	SceneTransitionManager,
	TextureManager
} from '../managers';
import { ApplianceDragManager } from '../managers/appliance-drag-manager';
import type { ApplianceDragOptions, ApplianceDragProvider } from '../managers/appliance-drag-manager';
import type { CutoutAdjust } from '../builders/kitchen-scene-builder/functions/generate-cutouts.cfn';
import type {
	ARCallbacks,
	ARExportOptions,
	CutoutInfo,
	EventCallbacks,
	ImageRenderOptions,
	ModelExportOptions,
	ModelLoadOptions,
	ObjectSnapshot,
	SelectionResult,
	TextureLoadCallbacks
} from '../managers';
import type {
	DataSource,
	EngineConfig,
	EngineEvent,
	EngineEventCallback,
	GeoLightingState,
	GeoLocation,
	KitchenDebugData,
	ObjectMetadata,
	QualityLevel,
	SceneComposition,
	TimePreset,
	Transform,
	WeatherPreset
} from '../types';
import type { ISceneBuilder } from '../builders';
import { KitchenSceneBuilder } from '../builders';
import type { TextureInfo } from '../../model/texture-info';
import {
	animateIslandObjectsByTypeCfn,
	applyCompositionCfn,
	applyCompositionSettingsAnimatedCfn,
	applyCompositionSettingsCfn,
	applyDiffImmediateCfn,
	hasStructuralChangesCfn,
	mergeCompositionsCfn,
	regenerateCutoutsForCurrentSceneCfn,
	setLayerVisibilityCfn,
	showBrightnessControlCfn,
	waitForSceneReadyCfn
} from './functions';
import { SceneCameraDelegate } from './delegates/scene-camera-delegate.class';
import { SceneDebugDelegate } from './delegates/scene-debug-delegate.class';
import { SceneObjectDelegate } from './delegates/scene-object-delegate.class';
import { SceneTextureDelegate } from './delegates/scene-texture-delegate.class';
import { ExportSceneUnavailableError, ExportValidationError } from '../types/export-errors';

/**
 * ThreeSceneEngine is the main 3D rendering engine.
 *
 * It composes all the managers to provide a unified API for:
 * - Scene setup and rendering
 * - Camera controls
 * - Lighting management
 * - Texture and material loading
 * - Model loading
 * - Event handling (click, selection)
 * - Debug visualization
 * - CSG cutout operations
 *
 * The engine is domain-agnostic - it knows nothing about kitchens,
 * blueprints, or specific scene content. Domain-specific logic is
 * handled by scene builders.
 *
 * @example
 * ```typescript
 * const engine = new ThreeSceneEngine({
 *   container: document.getElementById('canvas'),
 *   backgroundColor: '#ffffff',
 *   quality: 7
 * });
 *
 * engine.initialize();
 *
 * // Register scene builders
 * engine.registerBuilder('kitchen', new KitchenSceneBuilder());
 *
 * // Compose scene from data sources
 * await engine.compose([
 *   { builder: 'kitchen', data: kitchenBlueprint }
 * ]);
 *
 * // Cleanup
 * engine.dispose();
 * ```
 */
/**
 * The hover look. A pale cool white rather than a colour wash: it reads as "this is lit" on dark
 * stainless and on pale stone alike, and it stays clear of the drag palette, where white at 0.18 is
 * a lifted appliance and red is a refused drop.
 */
const HOVER_GLOW = { color: 0xa8dcff, intensity: 0.24 } as const;

/** A whole category lights harder than one object, because it is read across the whole kitchen. */
const CATEGORY_GLOW = { color: 0xa8dcff, intensity: 0.32 } as const;

/**
 * A vent is a hole: there is no surface to tint, only the proxy mesh standing in for the opening,
 * which sits in the scene switched off. Lighting one means switching that on — its material is
 * additive light, so it reads as the opening glowing rather than as a plug filling it.
 * @returns the proxy that was switched on, or null when there was nothing to switch
 */
const revealForGlow = (object: Object3D): Object3D | null => {
	if (object.userData?.type !== VENT_CUTOUT_TYPE || object.visible) return null;
	object.visible = true;
	// Read by CutoutManager.setDebugMode, which must not snuff a proxy a highlight is holding on.
	object.userData.glowing = true;
	return object;
};

/** Only a proxy that was off is ever switched on, so putting it back is putting it out. */
const hideAfterGlow = (object: Object3D | null): void => {
	if (!object) return;
	object.userData.glowing = false;
	object.visible = false;
};

export class ThreeSceneEngine {
	#config: EngineConfig;
	#qualitySettings: QualitySettings;
	#debugMode: boolean;

	// Core managers
	#sceneManager: SceneManager | null = null;
	#cameraManager: CameraManager | null = null;
	#lightingManager: LightingManager | null = null;
	#geoLightingManager: GeoLightingManager | null = null;
	#textureManager: TextureManager | null = null;
	#modelManager: ModelManager | null = null;
	#eventManager: EventManager | null = null;
	#debugManager: DebugManager | null = null;
	#cutoutManager: CutoutManager | null = null;
	#exportManager: ExportManager | null = null;
	#rotationGizmoManager: RotationGizmoManager | null = null;
	#applianceDragManager: ApplianceDragManager | null = null;
	/** One cutout preview job per island at a time; a newer request replaces a waiting one */
	#cutoutPreviewRunning: Map<string, Promise<void>> = new Map();
	#cutoutPreviewPending: Map<string, CutoutAdjust | undefined> = new Map();

	// Helper managers
	#animationManager: AnimationManager | null = null;
	#highlightManager: HighlightManager | null = null;
	#lightHelperManager: LightHelperManager | null = null;
	#cameraHelperManager: CameraHelperManager | null = null;

	// Brightness control (lazy-initialized on first showBrightnessControl(true))
	#brightnessControl: BrightnessControl | null = null;

	// Incremental update managers
	#sceneDiffManager: SceneDiffManager | null = null;
	#sceneTransitionManager: SceneTransitionManager | null = null;

	// Delegates
	#cameraDelegate: SceneCameraDelegate | null = null;
	#textureDelegate: SceneTextureDelegate | null = null;
	#debugDelegate: SceneDebugDelegate | null = null;
	#objectDelegate: SceneObjectDelegate | null = null;

	// Last kitchen debug data for resetView reuse
	#lastKitchenDebugData: KitchenDebugData | null = null;

	// Flag to track if initial scene has been built
	#hasInitialScene = false;

	// Current surround key (read/written via textureDelegate, kept for pre-delegate callers)
	// TODO: remove once all callers use textureDelegate

	// Last used data sources for rebuild
	#lastDataSources: DataSource[] = [];

	// Pending update to process after transition completes
	#pendingUpdate: { sources: DataSource[]; options?: { immediate?: boolean; forceRebuild?: boolean } } | null = null;

	// Flag to prevent concurrent progressive builds
	#isBuildingScene = false;

	// Monotonically incremented each time a new build starts. Lets build-phase
	// async continuations detect that a newer build superseded them.
	#buildGeneration = 0;

	// Last build error string for fast-fail in waitForSceneReady; cleared at compose start.
	#lastBuildError: string | null = null;

	// (sky upgrading state moved to textureDelegate)

	// Scene builders
	#builders = new Map<string, ISceneBuilder>();

	// Event callbacks
	#eventListeners = new Map<string, Set<EngineEventCallback>>();
	#hoverGlow: { objectId: string; backup: EmissiveTintBackup; revealed: Object3D | null } | null = null;
	#categoryGlow: Array<EmissiveTintBackup> = [];
	#categoryReveals: Array<Object3D> = [];

	// State
	#isInitialized = false;
	#isDisposed = false;

	// Render-loop activity tracking: true while camera-controls is between
	// wake and sleep events (user interaction or damping in progress).
	#isCameraActive = false;

	// Tracks whether camera-controls.update() reported movement this frame.
	// Read each frame inside onFrame(), consumed by shouldContinue().
	#cameraHasUpdated = false;

	// (sky texture state moved to textureDelegate)
	static readonly REFLECTION_SKY_KEY = 'sky/rainforest_trail';

	constructor (config: EngineConfig) {
		this.#config = config;

		// Determine quality and debug mode
		const rawQuality = config.quality ?? 7;
		const isDevelopmentMode = typeof window !== 'undefined' &&
			(window.location?.hostname === 'localhost' ||
			 window.location?.hostname === '127.0.0.1' ||
			 window.location?.hostname?.startsWith('192.168.'));

		// Debug mode: quality 0 or negative, OR running on localhost
		this.#debugMode = rawQuality < 0 || rawQuality === 0 || isDevelopmentMode;
		this.#qualitySettings = getQualitySettings(rawQuality);
	}

	// ========== Getters ==========

	/**
	 * Get the scene manager
	 */
	get sceneManager (): SceneManager | null {
		return this.#sceneManager;
	}

	/**
	 * Get the camera manager
	 */
	get cameraManager (): CameraManager | null {
		return this.#cameraManager;
	}

	/**
	 * Get the lighting manager
	 */
	get lightingManager (): LightingManager | null {
		return this.#lightingManager;
	}

	/**
	 * Get the geographic lighting manager
	 */
	get geoLightingManager (): GeoLightingManager | null {
		return this.#geoLightingManager;
	}

	/**
	 * Get the texture manager
	 */
	get textureManager (): TextureManager | null {
		return this.#textureManager;
	}

	/**
	 * Get the model manager
	 */
	get modelManager (): ModelManager | null {
		return this.#modelManager;
	}

	/**
	 * Get the event manager
	 */
	get eventManager (): EventManager | null {
		return this.#eventManager;
	}

	/**
	 * Get the debug manager
	 */
	get debugManager (): DebugManager | null {
		return this.#debugManager;
	}

	/**
	 * Get the cutout manager
	 */
	get cutoutManager (): CutoutManager | null {
		return this.#cutoutManager;
	}

	/**
	 * Get the rotation gizmo manager
	 */
	get rotationGizmoManager (): RotationGizmoManager | null {
		return this.#rotationGizmoManager;
	}

	/**
	 * Get the current presentation point (48" / 4' from active camera toward target)
	 */
	get sceneCenter (): Vector3 | null {
		return this.#cameraDelegate?.sceneCenter ?? null;
	}

	/**
	 * Set default presentation point and camera position from kitchen data
	 */
	setSceneCenterFromKitchenData (data: KitchenDebugData): void {
		this.#lastKitchenDebugData = data;
		this.#cameraDelegate?.setSceneCenterFromKitchenData(data);
	}

	/**
	 * Get the current presentation point (48" from auto-zoom position toward target)
	 */
	getCurrentPresentationPoint (): Vector3 | null {
		return this.#cameraDelegate?.getCurrentPresentationPoint() ?? null;
	}

	/**
	 * Set whether auto-zoom is enabled
	 */
	set autoZoomEnabled (enabled: boolean) {
		if (this.#cameraDelegate) this.#cameraDelegate.autoZoomEnabled = enabled;
	}

	/**
	 * Get the auto-zoom resting position
	 */
	get autoZoomPosition (): Vector3 | null {
		return this.#cameraDelegate?.autoZoomPosition ?? null;
	}

	/**
	 * Perform auto-zoom: animate camera to optimal position
	 */
	async performAutoZoom (animate = true, duration = 2000): Promise<void> {
		return this.#cameraDelegate?.performAutoZoom(animate, duration);
	}

	/**
	 * Get the export manager
	 */
	get exportManager (): ExportManager | null {
		return this.#exportManager;
	}

	/**
	 * Get the animation manager
	 */
	get animationManager (): AnimationManager | null {
		return this.#animationManager;
	}

	/** @internal Monotonically incrementing build generation token. */
	get buildGenerationAccess (): number {
		return this.#buildGeneration;
	}

	/** @internal True while a build is in flight. */
	get isBuildingSceneAccess (): boolean {
		return this.#isBuildingScene;
	}

	/** @internal True while a pending update is queued. */
	get hasPendingUpdateAccess (): boolean {
		return this.#pendingUpdate !== null;
	}

	/** @internal Last build error string; null if last build succeeded. */
	get lastBuildErrorAccess (): string | null {
		return this.#lastBuildError;
	}

	/** @internal True once dispose() has run — waiters must abandon a disposed engine. */
	get isDisposedAccess (): boolean {
		return this.#isDisposed;
	}

	/** @internal Returns the registered builder by name (for cfn functions). */
	getBuilderAccess (name: string): ISceneBuilder | undefined {
		return this.#builders.get(name);
	}

	/**
	 * Get the highlight manager
	 */
	get highlightManager (): HighlightManager | null {
		return this.#highlightManager;
	}

	/**
	 * Get the brightness control (null until first showBrightnessControl(true))
	 */
	get brightnessControl (): BrightnessControl | null {
		return this.#brightnessControl;
	}

	/**
	 * Show or hide the ambient brightness control sphere.
	 * Lazily instantiates BrightnessControl on first call with show=true.
	 */
	showBrightnessControl (show: boolean): void {
		showBrightnessControlCfn({
			show,
			scene: this.#sceneManager?.scene ?? null,
			camera: this.#sceneManager?.camera ?? null,
			domElement: this.#sceneManager?.renderer?.domElement ?? null,
			lightingManager: this.#lightingManager,
			cameraManager: this.#cameraManager,
			getBrightnessControl: () => this.#brightnessControl,
			setBrightnessControl: (control) => { this.#brightnessControl = control; }
		});
	}

	/**
	 * Set ambient brightness for the scene.
	 * @param value Brightness level from 0 (dark) to 1 (full brightness)
	 */
	setBrightness (value: number): void {
		this.#lightingManager?.applyCustomSkyBrightness(value);
		this.requestRender();
	}

	/**
	 * Restore scene brightness to default preset values.
	 */
	restoreBrightness (): void {
		this.#lightingManager?.restoreDefaultBrightness();
		this.requestRender();
	}

	/**
	 * Check if engine is initialized
	 */
	get isInitialized (): boolean {
		return this.#isInitialized;
	}

	/**
	 * Check if engine is active (rendering)
	 */
	get isActive (): boolean {
		return this.#sceneManager?.isActive ?? false;
	}

	/**
	 * Get debug mode
	 */
	get debugMode (): boolean {
		return this.#debugMode;
	}

	// ========== Initialization ==========

	/**
	 * Initialize the engine
	 * NOTE: This is async due to lazy-loaded camera-controls
	 */
	async initialize (): Promise<void> {
		if (this.#isInitialized) {
			return;
		}

		const { container, backgroundColor, quality = 7, xrEnabled = true, basePath = '' } = this.#config;
		// console.log('ThreeSceneEngine: Initializing with basePath:', basePath);

		// Create scene manager
		this.#sceneManager = new SceneManager({
			container,
			backgroundColor,
			quality: quality as QualityLevel,
			xrEnabled,
			gpuPreference: this.#config.gpuPreference,
			pixelRatio: this.#config.pixelRatio,
			antialias: this.#config.antialias,
			resizeDebounceHz: this.#config.resizeDebounceHz,
			onResizeSettled: () => this.emit('resize:settled', null)
		});
		this.#sceneManager.initialize();

		const { scene, camera, renderer } = this.#sceneManager;
		if (!scene || !camera || !renderer) {
			throw new Error('Failed to initialize scene manager');
		}

		// Start render loop IMMEDIATELY - scene is now visible (even if empty)
		// This enables progressive rendering and non-blocking initialization.
		//
		// shouldContinue keeps the loop alive while there is visual work:
		// - camera-controls is between wake/sleep (user interaction or damping)
		// - camera-controls.update() reported movement this frame
		// - AnimationManager has active animations
		//
		// When none of these are true the loop self-terminates.  It will be
		// restarted by camera wake events or requestRender() for one-shot
		// repaints (texture swaps, @Publish events -- wired in Part 1.4).
		this.#sceneManager.startRenderLoop(
			(delta) => { this.onFrame(delta); },
			() => {
				const hasAnimations = (this.#animationManager?.count ?? 0) > 0;
				return this.#isCameraActive || this.#cameraHasUpdated || hasAnimations;
			}
		);
		this.emit('init:renderer-ready', null);

		// Start camera controls loading in parallel (non-blocking to scene display)
		const cameraPromise = CameraManager.create({
			camera,
			domElement: renderer.domElement,
			debugMode: this.#debugMode
		}).then((cameraManager) => {
			this.#cameraManager = cameraManager;
			this.emit('init:camera-ready', null);
			return cameraManager;
		});

		// Create lighting manager
		this.#lightingManager = new LightingManager({
			scene,
			shadowsEnabled: this.#qualitySettings.shadowMapEnabled,
			shadowMapSize: 2048
		});
		this.#lightingManager.initialize();

		// Create geographic lighting manager (integrates with existing lights)
		this.#geoLightingManager = new GeoLightingManager({
			scene,
			ambientLight: this.#lightingManager.ambientLight,
			directionalLight: this.#lightingManager.directionalLight,
			hemisphereLight: this.#lightingManager.hemisphereLight
		});

		// Create texture manager.
		// Default transcoder path points at the jsdelivr copy matching the
		// bundled three.js (0.163.0). Consumers can override or pass `null`
		// to disable KTX2 entirely.
		const ktx2TranscoderPath = this.#config.ktx2TranscoderPath === null
			? undefined
			: (this.#config.ktx2TranscoderPath ?? 'https://cdn.jsdelivr.net/npm/three@0.163.0/examples/jsm/libs/basis/');
		this.#textureManager = new TextureManager({
			basePath,
			renderQuality: Math.abs(quality),
			textureQuality: this.#config.textureQuality ?? null,
			renderer,
			qualitySettings: this.#qualitySettings,
			ktx2TranscoderPath
		});

		// Create model manager
		this.#modelManager = new ModelManager({
			basePath,
			qualitySettings: this.#qualitySettings,
			qualityLevel: Math.abs(quality)
		});

		// Create event manager
		this.#eventManager = new EventManager({
			domElement: renderer.domElement,
			scene,
			camera,
			debugMode: this.#debugMode
		});

		// Setup event forwarding
		this.#eventManager.setCallbacks({
			onSelect: (result) => this.emit('object:clicked', result),
			onDoubleClick: (result) => {
				this.emit('object:dbl-clicked', result);
			},
			onRightClick: (result) => this.emit('object:right-clicked', result)
		});

		// Create debug manager
		this.#debugManager = new DebugManager({
			scene,
			camera,
			renderer,
			visible: this.#debugMode
		});

		// Create cutout manager
		this.#cutoutManager = new CutoutManager({
			qualitySettings: this.#qualitySettings
		});

		// Create export manager
		this.#exportManager = new ExportManager({
			renderer,
			scene,
			camera,
			islandHolder: this.#sceneManager.objectHolder,
			carpet: null, // Will be set later when floor is added
			backgroundColor: null,
			skyTextureKey: 'sky/overcast',
			environmentMapTexture: null
		});

		// Create rotation gizmo manager
		this.#rotationGizmoManager = new RotationGizmoManager({
			scene,
			camera,
			domElement: renderer.domElement,
			emitEvent: (type, data) => this.emit(type, data),
			onInteractionStart: () => {
				if (this.#cameraManager?.controls) {
					this.#cameraManager.controls.enabled = false;
				}
			},
			onInteractionEnd: () => {
				if (this.#cameraManager?.controls) {
					this.#cameraManager.controls.enabled = true;
				}
			}
		});

		// Create helper managers
		this.#animationManager = new AnimationManager();

		// Wake the demand-driven render loop whenever any animation starts.
		// The loop self-terminates when idle (no camera activity, no
		// animations); an animation started while it sleeps would otherwise
		// never tick — the scene stays visually frozen and completion promises
		// hang — until user interaction fires a camera wake event.
		this.#animationManager.setOnAnimationStart(() => {
			this.#sceneManager?.renderScheduler?.startLoop();
		});

		// Press-and-hold drag of appliances (mesh-only preview; the controller commits on drop)
		this.#applianceDragManager = new ApplianceDragManager({
			scene,
			camera,
			domElement: renderer.domElement,
			animationManager: this.#animationManager,
			getObjectHolder: () => this.#sceneManager?.objectHolder ?? null,
			requestRender: () => this.requestRender(),
			emitEvent: (type, data) => this.emit(type, data),
			onInteractionStart: () => {
				if (this.#cameraManager?.controls) this.#cameraManager.controls.enabled = false;
			},
			onInteractionEnd: () => {
				if (this.#cameraManager?.controls) this.#cameraManager.controls.enabled = true;
			},
			highlightObject: (objectId) => this.ensureHighlight(objectId),
			followHighlight: (objectId) => this.followHighlight(objectId),
			previewCutouts: (islandId, adjust) => this.previewIslandCutouts(islandId, adjust),
			cancelCutoutPreviews: () => this.cancelPendingCutoutPreviews(),
			suppressClick: () => {
				if (this.#eventManager) this.#eventManager.internalPointerDragged = true;
			},
			isBusy: () => this.#isBuildingScene
		});

		this.#highlightManager = new HighlightManager({
			scene,
			animationManager: this.#animationManager,
			debugMode: this.#debugMode,
			// Fade out directional light when first highlight is added
			onFirstHighlightAdded: () => {
				this.fadeDirectionalLight(false);
			},
			// Fade in directional light when last highlight is removed
			onLastHighlightRemoved: () => {
				this.fadeDirectionalLight(true);
			}
		});

		this.#lightHelperManager = new LightHelperManager({
			scene,
			visible: this.#debugMode,
			camera,
			domElement: renderer.domElement,
			quality: Math.abs(quality) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10,
			debugManager: this.debugManager ?? undefined
		});

		// Enable light interaction in debug mode (color picker, intensity drag, etc.)
		if (this.#debugMode) {
			this.#lightHelperManager.enableInteraction();

			// Set up callbacks to disable camera controls during light interaction
			this.#lightHelperManager.setOnInteractionStart(() => {
				if (this.#cameraManager?.controls) {
					this.#cameraManager.controls.enabled = false;
				}
			});
			this.#lightHelperManager.setOnInteractionEnd(() => {
				if (this.#cameraManager?.controls) {
					this.#cameraManager.controls.enabled = true;
				}
			});
		}

		this.#cameraHelperManager = new CameraHelperManager({
			scene,
			visible: this.#debugMode,
			quality: Math.abs(quality) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
		});
		this.#cameraHelperManager.registerCamera(camera);

		// Create incremental update managers
		this.#sceneDiffManager = new SceneDiffManager({
			positionTolerance: 0.1
		});

		this.#sceneTransitionManager = new SceneTransitionManager({
			animationManager: this.#animationManager,
			objectHolder: this.#sceneManager.objectHolder,
			fadeOutDuration: this.#config.dissolveOutDuration ?? 400,
			moveDuration: 600,
			fadeInDuration: this.#config.progressiveFadeInDuration ?? 400,
			fadeOutScale: 0.8,
			onCutoutRegenerationNeeded: async () => {
				await this.regenerateCutoutsForCurrentScene();
			},
			debugManager: this.#debugManager ?? undefined
		});

		// Wait for camera controls to complete loading
		await cameraPromise;

		// Wire camera-controls wake/sleep events to the render scheduler.
		// wake  -> start the continuous render loop (user interaction or damping)
		// sleep -> the loop will self-terminate via shouldContinue returning false
		if (this.#cameraManager) {
			const { renderScheduler } = this.#sceneManager;
			this.#cameraManager.setWakeSleepCallbacks(
				() => {
					this.#isCameraActive = true;
					renderScheduler?.startLoop();
				},
				() => {
					this.#isCameraActive = false;
					// The loop continues until shouldContinue returns false,
					// which now happens naturally when #isCameraActive is false
					// and #cameraHasUpdated is false and no animations remain.
				}
			);
		}

		// Create camera delegate (needs cameraManager from resolved promise)
		this.#cameraDelegate = new SceneCameraDelegate({
			cameraManager: this.#cameraManager,
			debugManager: this.#debugManager,
			animationManager: this.#animationManager,
			autoZoomEnabled: this.#config.autoZoom ?? true,
			autoZoomPadding: this.#config.autoZoomPadding,
			autoZoomCameraHeightBoost: this.#config.autoZoomCameraHeightBoost,
			autoZoomStyle: this.#config.autoZoomStyle
		});

		// Create debug delegate
		this.#debugDelegate = new SceneDebugDelegate({
			debugManager: this.#debugManager,
			lightingManager: this.#lightingManager,
			highlightManager: this.#highlightManager,
			lightHelperManager: this.#lightHelperManager,
			cameraHelperManager: this.#cameraHelperManager,
			cameraManager: this.#cameraManager,
			sceneManager: this.#sceneManager,
			cutoutManager: this.#cutoutManager,
			eventManager: this.#eventManager,
			getCameraDelegate: () => this.#cameraDelegate
		});

		// Create texture delegate
		this.#textureDelegate = new SceneTextureDelegate({
			textureManager: this.#textureManager,
			sceneManager: this.#sceneManager,
			modelManager: this.#modelManager,
			exportManager: this.#exportManager,
			animationManager: this.#animationManager,
			lightingManager: this.#lightingManager,
			config: this.#config,
			qualitySettings: this.#qualitySettings,
			builders: this.#builders,
			requestRender: () => this.requestRender()
		});

		// Create object delegate
		this.#objectDelegate = new SceneObjectDelegate({
			sceneManager: this.#sceneManager,
			sceneTransitionManager: this.#sceneTransitionManager,
			animationManager: this.#animationManager,
			sceneDiffManager: this.#sceneDiffManager,
			emit: (type, data) => this.emit(type, data),
			rebuild: (sources?) => this.rebuild(sources),
			updateScene: (sources?) => this.updateScene(sources || this.#lastDataSources),
			getLastDataSources: () => this.#lastDataSources,
			changeSurroundTexture: (key) => this.changeSurroundTexture(key),
			changeCountertopTexture: (key) => this.changeCountertopTexture(key),
			changeFloorTexture: (key) => this.changeFloorTexture(key),
			regenerateCutouts: () => this.regenerateCutoutsForCurrentScene(),
			regenerateIslandCutouts: (islandId) => this.previewIslandCutouts(islandId),
			updateSceneSkipStructural: (sources) => this.updateScene(sources, { skipStructuralCheck: true }),
			rebuildIslandGroup: (islandId) => this.#rebuildIslandGroup(islandId),
			repositionCamera: () => this.#repositionCameraForCurrentScene()
		});

		this.#isInitialized = true;
		this.emit('init:complete', null);
	}

	// ========== Scene Foundation ==========

	/**
	 * Load sky and floor textures as the scene's foundation layer.
	 * This should be called before any scene build so the sky background
	 * and floor are visible immediately while objects load.
	 *
	 * - Loads default-resolution textures (blocking)
	 * - Applies sky as scene background/environment
	 * - Creates a foundation floor mesh
	 * - Kicks off async quality upgrade for sky (non-blocking)
	 *
	 * The TextureManager caches loaded textures, so when the builder runs
	 * later it will skip reloading these textures. The foundation floor mesh
	 * is tagged with `userData.isFoundation = true` and will be removed when
	 * the builder adds its own floor.
	 */
	async loadSceneFoundation (options: {
		skyKey?: string;
		floorKey?: string;
		quality?: number;
		/** Default camera distance from center in feet (default 30) */
		defaultCameraDistance?: number;
	} = {}): Promise<void> {
		// Set default camera position if not already set
		if (this.#cameraManager && !this.#cameraDelegate?.defaultCameraPosition) {
			const distanceFeet = options.defaultCameraDistance ?? 30;
			const distanceCm = distanceFeet * 30.48;
			const defaultPos = new Vector3(0, Math.round(distanceCm * 0.259), Math.round(distanceCm * 0.966));
			const defaultTarget = new Vector3(0, 18, 0);
			if (this.#cameraDelegate) {
				this.#cameraDelegate.defaultCameraPosition = defaultPos.clone();
				this.#cameraDelegate.defaultCameraTarget = defaultTarget.clone();
			}
			this.#cameraManager.setPosition(defaultPos.x, defaultPos.y, defaultPos.z, defaultTarget.x, defaultTarget.y, defaultTarget.z, false);
		}

		await this.#textureDelegate?.loadSceneFoundation(
			{ skyKey: options.skyKey, floorKey: options.floorKey, quality: options.quality },
			this.#isDisposed
		);
	}

	/**
	 * Remove the foundation floor mesh (tagged with isFoundation).
	 */
	removeFoundationFloor (): void {
		this.#textureDelegate?.removeFoundationFloor();
	}

	// ========== Scene Composition ==========

	/**
	 * Register a scene builder
	 */
	/**
	 * Light up every part of the kitchen in one category at once — what a legend entry points at.
	 * Pass null to put them all back.
	 * @param category the group to light, or null to clear
	 */
	setCategoryHighlight (category: SceneCategory | null): void {
		for (const backup of this.#categoryGlow) restoreEmissiveTint(backup);
		this.#categoryGlow = [];
		for (const object of this.#categoryReveals) hideAfterGlow(object);
		this.#categoryReveals = [];

		const types = category ? new Set(SCENE_CATEGORY_TYPES[category] ?? []) : null;
		if (types) {
			const islands = (this.#sceneManager?.objectHolder?.children ?? []).filter((child) => child.userData?.type === 'island');
			for (const islandGroup of islands) {
				islandGroup.traverse((child) => {
					if (!types.has(child.userData?.type as string)) return;
					const revealed = revealForGlow(child);
					if (revealed) {
						this.#categoryReveals.push(revealed);
						return;
					}
					this.#categoryGlow.push(applyEmissiveTint(child, CATEGORY_GLOW.color, CATEGORY_GLOW.intensity));
				});
			}
		}
		this.requestRender();
	}

	/**
	 * Track what the pointer is over. Off by default: hovering raycasts the scene, so only a consumer
	 * that wants `object:hovered` should pay for it. While on, a type registered with `hoverGlow`
	 * lights up under the pointer.
	 * @param enabled whether to follow the pointer
	 */
	setHoverTrackingEnabled (enabled: boolean): void {
		if (!enabled) {
			this.#eventManager?.setCallbacks({ onHover: undefined });
			this.#clearHoverGlow();
			this.emit('object:hovered', null);
			return;
		}
		this.#eventManager?.setCallbacks({ onHover: (result) => this.#handleHover(result) });
	}

	#handleHover (result: SelectionResult | null): void {
		const objectId = result?.objectId ?? null;
		if ((this.#hoverGlow?.objectId ?? null) === objectId) return;

		this.#clearHoverGlow();
		if (result && isHoverGlowObjectType(result.object?.userData?.type)) {
			this.#hoverGlow = {
				objectId: result.objectId,
				backup: applyEmissiveTint(result.object, HOVER_GLOW.color, HOVER_GLOW.intensity),
				revealed: revealForGlow(result.object)
			};
		}
		this.requestRender();
		this.emit('object:hovered', result);
	}

	#clearHoverGlow (): void {
		if (!this.#hoverGlow) return;
		restoreEmissiveTint(this.#hoverGlow.backup);
		hideAfterGlow(this.#hoverGlow.revealed);
		this.#hoverGlow = null;
	}

	registerBuilder (name: string, builder: ISceneBuilder): void {
		this.#builders.set(name, builder);
	}

	/**
	 * Unregister a scene builder
	 */
	unregisterBuilder (name: string): void {
		const builder = this.#builders.get(name);
		if (builder?.dispose) {
			builder.dispose();
		}
		this.#builders.delete(name);
	}

	/**
	 * Compose scene from data sources (full rebuild)
	 */
	async compose (sources: DataSource[]): Promise<void> {
		if (this.#isDisposed) return;
		this.#buildGeneration++;
		this.#lastBuildError = null;
		this.#textureManager?.clearFailedKeys();
		// Store sources for rebuild
		this.#lastDataSources = sources;
		// Readiness/self-heal must see a running build — without this flag a build in progress
		// is indistinguishable from a stranded scene and self-heal fires racing composes into it
		this.#isBuildingScene = true;
		try {

		// Pre-set surround key from data sources BEFORE build starts
		// This prevents false "slat transition" detection when changeSurroundTexture is called after initial render
		for (const source of sources) {
			const data = source.data as { kitchen?: { material?: { surround?: { key?: string } } } } | undefined;
			const surroundKey = data?.kitchen?.material?.surround?.key;
			if (surroundKey) {
				if (this.#textureDelegate) this.#textureDelegate.currentSurroundKey = surroundKey;
				break;
			}
		}

		this.emit('render:start', { isRebuild: true });

		// Keep the render loop running during and after the build so the
		// scene remains interactive. Without this, the demand-driven
		// scheduler self-terminates and deferred GPU work (PMREM) blocks
		// the first user interaction.
		this.#sceneManager?.renderScheduler?.startLoop();

		for (const source of sources) {
			const builder = this.#builders.get(source.builder);
			if (!builder) {
				console.warn(`No builder registered for: ${source.builder}`);
				continue;
			}

			try {
				const composition = await builder.build(source.data);
				await this.applyComposition(composition, source.layer);
			} catch (error) {
				const msg = error instanceof Error ? error.message : String(error);
				console.error(`Failed to build scene from ${source.builder}:`, error);
				this.#lastBuildError = msg;
				this.emit('render:error', { error: msg });
			}
		}

		// Snapshot scene state for future incremental updates
		if (this.#sceneDiffManager && this.#sceneManager?.objectHolder) {
			// console.log('ThreeSceneEngine.compose: Taking snapshot of scene for incremental updates');
			this.#sceneDiffManager.snapshotScene(this.#sceneManager.objectHolder);
			this.#hasInitialScene = true;
			// console.log('ThreeSceneEngine.compose: Snapshot complete, _hasInitialScene =', this.#hasInitialScene);
		} else {
			console.warn('ThreeSceneEngine.compose: Cannot take snapshot', {
				hasDiffManager: Boolean(this.#sceneDiffManager),
				hasObjectHolder: Boolean(this.#sceneManager?.objectHolder)
			});
		}

		// Scene objects are attached and verified here — a never-settling auto-zoom below must not wedge #isBuildingScene=true and hang every waitForSceneReady
		this.#isBuildingScene = false;
		this.emit('render:complete', { isRebuild: true });

		// Run PMREM generation NOW, during the build phase, before the user
		// can interact. fromEquirectangular() is a synchronous GPU call that
		// blocks ~200ms. Running it here (while the scene is still settling)
		// prevents the freeze-after-first-drag that happens when it runs
		// from requestIdleCallback after the render loop self-terminates.
		this.ensureEnvironmentMapForReflections();

		// Fire-and-forget: auto-zoom's onComplete never fires if the demand-driven render loop idles out, and awaiting the stalled animation would hang compose forever
		if (this.#cameraDelegate?.isAutoZoomEnabled && this.#cameraDelegate?.lastSceneBounds) {
			if (this.#cameraDelegate.autoZoomStyle === 'settle') {
				// Already framed before the build; this only trues up the framing
				// against the bounds the finished scene actually occupies.
				this.#cameraDelegate.settleAutoZoom();
			} else {
				void this.performAutoZoom(true, 2000).catch(() => undefined);
			}
		}

		// Ensure at least one frame renders after composition settles
		this.requestRender();

		// Process any pending update that was queued during this compose
		await this.processPendingUpdate();
		} finally {
			this.#isBuildingScene = false;
		}
	}

	/**
	 * Compose scene progressively - objects appear as they're built.
	 * This provides a non-blocking user experience where the scene is visible
	 * and updating as data is loaded and processed.
	 *
	 * Objects fade in as they're yielded from the builder's buildProgressive() generator.
	 * Falls back to standard compose() if the builder doesn't support progressive building.
	 *
	 * @param sources - Data sources to compose from
	 */
	async composeProgressive (sources: DataSource[]): Promise<void> {
		this.#buildGeneration++;
		this.#lastBuildError = null;
		this.#textureManager?.clearFailedKeys();
		// Track if camera was already positioned BEFORE build started
		// This prevents re-animating to a position that was already set by setSceneCenterFromKitchenData
		const cameraPrePositioned = Boolean(this.#cameraDelegate?.defaultCameraPosition && this.#cameraDelegate?.defaultCameraTarget);

		// Store sources for rebuild
		this.#lastDataSources = sources;

		this.emit('render:start', { isRebuild: true });

		// Ensure the render loop is running during the build so progressive
		// fade-in animations play smoothly.  The loop will self-terminate
		// after all animations complete and the camera is idle.
		this.#sceneManager?.renderScheduler?.startLoop();

		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder) {
			console.error('ThreeSceneEngine.composeProgressive: No object holder available');
			this.emit('render:complete', { isRebuild: true });
			return;
		}

		// Pre-set surround key from data sources BEFORE progressive build starts
		// This prevents false "slat transition" detection when changeSurroundTexture is called after initial render
		for (const source of sources) {
			const data = source.data as { kitchen?: { material?: { surround?: { key?: string } } } } | undefined;
			const surroundKey = data?.kitchen?.material?.surround?.key;
			if (surroundKey) {
				if (this.#textureDelegate) this.#textureDelegate.currentSurroundKey = surroundKey;
				break;
			}
		}

		let finalComposition: SceneComposition | null = null;

		for (const source of sources) {
			const builder = this.#builders.get(source.builder);
			if (!builder) {
				console.warn(`No builder registered for: ${source.builder}`);
				continue;
			}

			try {
				// Check if builder supports progressive building
				if (!builder.buildProgressive) {
					// Fallback to standard compose for this builder
					const composition = await builder.build(source.data);
					await this.applyComposition(composition, source.layer);
					finalComposition = composition;
					continue;
				}

				// Use progressive building
				const generator = builder.buildProgressive(source.data);
				const animConfig = this.#config.progressiveAnimation ?? {};
				const addEffect = animConfig.addEffect ?? 'fade';
				const addDuration = animConfig.addDuration ?? this.#config.progressiveFadeInDuration ?? 300;
				const scaleFrom = animConfig.scaleFrom ?? 0.8;
				const shouldAnimate = this.#config.progressiveRender !== false && addEffect !== 'none';

				// Track if camera was already set via setup object
				let cameraAlreadyApplied = false;

				// Process objects as they're yielded
				let result = await generator.next();
				while (!result.done) {
					// Type assertion: while not done, value is SceneObject
					const sceneObject = result.value as import('../types').SceneObject;

					// Handle setup object - apply camera/bounds immediately before any rendering
					if (sceneObject.metadata.type === 'setup') {
						const setupMeta = sceneObject.metadata as any;
						if (setupMeta.camera && setupMeta.bounds) {
							await this.applyCompositionSettings({
								objects: [],
								camera: setupMeta.camera,
								bounds: setupMeta.bounds
							});
							// Frame the scene before a single object renders, so the
							// build is watched from where the camera will end up
							// rather than from the foundation's default position.
							if (this.#cameraDelegate?.isAutoZoomEnabled && this.#cameraDelegate.autoZoomStyle === 'settle') {
								this.#cameraDelegate.settleAutoZoom(setupMeta.bounds);
							}
							cameraAlreadyApplied = true;
						}
						// Track sky key and set environment map IMMEDIATELY
						// This ensures sky background is visible before kitchen geometry loads
						if (setupMeta.skyKey) {
							if (this.#textureDelegate) this.#textureDelegate.currentSkyKey = setupMeta.skyKey;
							this.ensureEnvironmentMapForReflections();
						}
						result = await generator.next();
						continue;
					}

					// Sky quality upgrades skipped — blocks main thread (see compose comment)
					if (sceneObject.metadata.type === 'sky-quality-upgrade') {
						result = await generator.next();
						continue;
					}

					// Remove foundation floor when builder provides its own
					if (sceneObject.metadata.type === 'floor') {
						this.removeFoundationFloor();
					}

					// Add object with optional animation
					sceneObject.object.userData = {
						...sceneObject.object.userData,
						...sceneObject.metadata
					};

					if (shouldAnimate && this.#animationManager) {
						// Create a reference for the closure
						const objForAnimation = sceneObject.object;
						const animId = `progressive-${sceneObject.metadata.id}-${Date.now()}`;

						// Prepare for animation based on effect type
						const usesFade = addEffect === 'fade' || addEffect === 'fade-scale';
						const usesScale = addEffect === 'scale' || addEffect === 'fade-scale';

						if (usesFade) {
							this.prepareMaterialsForFadeProgressive(objForAnimation);
							this.setObjectOpacityProgressive(objForAnimation, 0);
						}

						if (usesScale) {
							objForAnimation.scale.setScalar(scaleFrom);
						}

						// Add to scene only if not already parented (e.g., panels are children of island groups)
						if (!objForAnimation.parent) {
							objectHolder.add(objForAnimation);
						}

						// Start animations (don't await - let them run while we continue)
						const animPromises: Promise<void>[] = [];

						if (usesFade) {
							animPromises.push(this.#animationManager.animateOpacity(
								`${animId}-opacity`,
								0,
								1,
								addDuration,
								(opacity) => this.setObjectOpacityProgressive(objForAnimation, opacity)
							).then(() => this.restoreMaterialsProgressive(objForAnimation)));
						}

						if (usesScale) {
							animPromises.push(this.#animationManager.animateScale(
								`${animId}-scale`,
								scaleFrom,
								1,
								addDuration,
								(scale) => objForAnimation.scale.setScalar(scale)
							));
						}
					} else {
						// No animation - add immediately (only if not already parented)
						if (!sceneObject.object.parent) {
							objectHolder.add(sceneObject.object);
						}
					}

					// Emit event for each object added
					this.emit('object:added', {
						id: sceneObject.metadata.id,
						type: sceneObject.metadata.type
					});

					result = await generator.next();
				}

				// Generator return value is the final composition with bounds/camera
				// Type assertion: when done, value is SceneComposition
				finalComposition = result.value as SceneComposition;

				// Apply camera and bounds from final composition (only if not already applied)
				// Skip if: camera was set via setup object OR camera was pre-positioned before build started
				const shouldApplyCamera = finalComposition && !cameraAlreadyApplied && !cameraPrePositioned;
				if (shouldApplyCamera) {
					const cameraTransitionDuration = animConfig.cameraTransitionDuration ?? 800;
					await this.applyCompositionSettingsAnimated(finalComposition, cameraTransitionDuration);
				}
			} catch (error) {
				const msg = error instanceof Error ? error.message : String(error);
				console.error(`Failed to build scene progressively from ${source.builder}:`, error);
				this.#lastBuildError = msg;
				this.emit('render:error', { error: msg });
			}
		}

		// Snapshot scene state for future incremental updates
		if (this.#sceneDiffManager && this.#sceneManager?.objectHolder) {
			this.#sceneDiffManager.snapshotScene(this.#sceneManager.objectHolder);
			this.#hasInitialScene = true;
		}

		// Ensure environment map is set for metallic reflections
		this.ensureEnvironmentMapForReflections();

		this.emit('render:complete', { isRebuild: true });

		// Auto-zoom: slide camera from source to fit position, or true up the
		// framing the scene was already built inside.
		if (this.#cameraDelegate?.isAutoZoomEnabled && this.#cameraDelegate?.lastSceneBounds) {
			if (this.#cameraDelegate.autoZoomStyle === 'settle') {
				this.#cameraDelegate.settleAutoZoom();
			} else {
				await this.performAutoZoom(true, 2000);
			}
		}

		// Ensure at least one frame renders after progressive composition settles
		this.requestRender();

		// Process any pending update that was queued during this progressive compose
		await this.processPendingUpdate();
	}

	/**
	 * Recovery rebuild from the last composed data sources. Used by waitForSceneReady's
	 * self-heal path when a build was aborted by a competing generation bump and the
	 * scene is verifiably incomplete with nothing in flight.
	 */
	async recomposeFromLastSources (): Promise<void> {
		if (this.#isDisposed) return;
		if (!this.#lastDataSources || this.#lastDataSources.length === 0) return;
		console.warn('[Engine] self-heal: recomposing scene from last data sources');
		await this.compose(this.#lastDataSources);
	}

	/**
	 * Compose scene progressively with type-based animations.
	 *
	 * This method uses the SceneTransitionManager's type-aware animation system
	 * to animate objects based on their type (panel, appliance, countertop, etc.).
	 *
	 * Islands are processed sequentially, with objects within each island animated
	 * according to the configured type order and parallel groups.
	 *
	 * @param sources - Data sources to compose from
	 */
	async composeProgressiveWithTypes (sources: DataSource[]): Promise<void> {
		// Guard against concurrent builds - if already building, queue for later
		if (this.#isBuildingScene) {
			this.#pendingUpdate = { sources };
			return;
		}
		this.#isBuildingScene = true;
		this.#buildGeneration++;
		this.#lastBuildError = null;
		this.#textureManager?.clearFailedKeys();

		// Track if camera was already positioned BEFORE build started
		// This prevents re-animating to a position that was already set by setSceneCenterFromKitchenData
		const cameraPrePositioned = Boolean(this.#cameraDelegate?.defaultCameraPosition && this.#cameraDelegate?.defaultCameraTarget);

		// Store sources for rebuild
		this.#lastDataSources = sources;

		this.emit('render:start', { isRebuild: true });

		// Ensure the render loop is running during the build so progressive
		// fade-in animations play smoothly.  The loop will self-terminate
		// after all animations complete and the camera is idle.
		this.#sceneManager?.renderScheduler?.startLoop();

		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder) {
			console.error('ThreeSceneEngine.composeProgressiveWithTypes: No object holder available');
			this.#isBuildingScene = false;
			this.emit('render:complete', { isRebuild: true });
			return;
		}

		// Pre-set sky key from data sources BEFORE progressive build starts
		// This ensures the sky is shown on the first rendered frame
		for (const source of sources) {
			const data = source.data as { skyKey?: string } | undefined;
			if (data?.skyKey) {
				if (this.#textureDelegate) this.#textureDelegate.currentSkyKey = data.skyKey;
				// Try to set environment map immediately if textures are already loaded
				this.ensureEnvironmentMapForReflections();
				break;
			}
		}

		// Pre-set surround key from data sources BEFORE progressive build starts
		// This prevents false "slat transition" detection when changeSurroundTexture is called after initial render
		for (const source of sources) {
			const data = source.data as { kitchen?: { material?: { surround?: { key?: string } } } } | undefined;
			const surroundKey = data?.kitchen?.material?.surround?.key;
			if (surroundKey) {
				if (this.#textureDelegate) this.#textureDelegate.currentSurroundKey = surroundKey;
				break;
			}
		}

		let finalComposition: SceneComposition | null = null;

		// Track island groups for type-based animations
		const islandGroups = new Map<string, {
			center: { x: number; y: number; z: number };
			objects: import('../types').SceneObject[];
		}>();

		for (const source of sources) {
			const builder = this.#builders.get(source.builder);
			if (!builder) {
				console.warn(`No builder registered for: ${source.builder}`);
				continue;
			}

			try {
				// console.log(`[ENGINE] Starting progressive build for source: ${source.builder}`);

				// Check if builder supports progressive building
				if (!builder.buildProgressive) {
					// Fallback to standard compose for this builder
					const composition = await builder.build(source.data);
					await this.applyComposition(composition, source.layer);
					finalComposition = composition;
					continue;
				}

				// Use progressive building with type-based animations
				const generator = builder.buildProgressive(source.data);

				// Collect objects by type for batched animation
				let currentIslandId: string | null = null;
				let currentIslandObjects: import('../types').SceneObject[] = [];

				// Wave-based parallel animation tracking
				let currentWave: number | null = null;
				// Map of islandId -> { objects: SceneObject[], islandData: {...} }
				const waveIslands = new Map<string, {
					objects: import('../types').SceneObject[];
					islandData: { center: { x: number; y: number; z: number }; objects: import('../types').SceneObject[] };
				}>();

				// Fire-and-forget appliance slide-in promises so multiple appliances
				// animate in parallel. Awaited together at the end of the build so
				// render:complete still reflects full scene readiness.
				const applianceAnimations: Array<Promise<void>> = [];

				// Track if camera was already set via setup object
				let cameraAlreadyApplied = false;

				// Process objects as they're yielded
				let result = await generator.next();
				while (!result.done) {
					const sceneObject = result.value as import('../types').SceneObject;
					const { metadata, object } = sceneObject;

					// Handle setup object - apply camera/bounds immediately before any rendering
					if (metadata.type === 'setup') {
						const setupMeta = metadata as any;
						if (setupMeta.camera && setupMeta.bounds) {
							await this.applyCompositionSettings({
								objects: [],
								camera: setupMeta.camera,
								bounds: setupMeta.bounds
							});
							// Frame the scene before a single object renders, so the
							// build is watched from where the camera will end up
							// rather than from the foundation's default position.
							if (this.#cameraDelegate?.isAutoZoomEnabled && this.#cameraDelegate.autoZoomStyle === 'settle') {
								this.#cameraDelegate.settleAutoZoom(setupMeta.bounds);
							}
							cameraAlreadyApplied = true;
						}
						// Track sky key and set environment map IMMEDIATELY
						// This ensures sky background is visible before kitchen geometry loads
						if (setupMeta.skyKey) {
							if (this.#textureDelegate) this.#textureDelegate.currentSkyKey = setupMeta.skyKey;
							this.ensureEnvironmentMapForReflections();
						}
						result = await generator.next();
						continue;
					}

					// Sky quality upgrades skipped — blocks main thread (see compose comment)
					if (metadata.type === 'sky-quality-upgrade') {
						result = await generator.next();
						continue;
					}

					// Set userData
					object.userData = { ...object.userData, ...metadata };

					// Handle different object types
					if (metadata.type === 'floor') {
						// Remove foundation floor before adding the builder's floor
						this.removeFoundationFloor();
						// Floor is added immediately without animation
						objectHolder.add(object);
						this.emit('object:added', { id: metadata.id, type: metadata.type });
					} else if (metadata.type === 'island') {
						const islandWave = (metadata as any).wave ?? 0;
						// First, add previous island's objects to waveIslands (if any)
						if (currentIslandId && currentIslandObjects.length > 0) {
							const prevIslandData = islandGroups.get(currentIslandId);
							if (prevIslandData) {
								waveIslands.set(currentIslandId, { objects: [...currentIslandObjects], islandData: prevIslandData });
							}
							currentIslandObjects = [];
						}

						// If wave changed, animate all islands from the previous wave in parallel
						if (currentWave !== null && islandWave !== currentWave && waveIslands.size > 0) {
							const waveAnimations = Array.from(waveIslands.values()).map(({ objects, islandData }) => this.animateIslandObjectsByType(objects, islandData));
							await Promise.all(waveAnimations);
							waveIslands.clear();
						}

						currentWave = islandWave;

						// Add island group to scene
						objectHolder.add(object);
						currentIslandId = metadata.id ?? null;

						// Store island data for animations
						const center = object.userData?.center || { x: 0, y: 0, z: 0 };
						islandGroups.set(metadata.id ?? 'unknown', { center, objects: [] });

						this.emit('object:added', { id: metadata.id, type: metadata.type });
					} else if (metadata.type === 'flush-islands') {
						// Explicit flush marker - animate all pending island geometry NOW
						// This ensures the final wave gets animated before cutouts/appliances

						// Add current island to waveIslands if it has objects
						if (currentIslandId && currentIslandObjects.length > 0) {
							const prevIslandData = islandGroups.get(currentIslandId);
							if (prevIslandData) {
								waveIslands.set(currentIslandId, { objects: [...currentIslandObjects], islandData: prevIslandData });
							}
							currentIslandObjects = [];
						}

						// Animate all remaining wave islands in parallel
						if (waveIslands.size > 0) {
							const waveAnimations = Array.from(waveIslands.values()).map(({ objects, islandData }) => this.animateIslandObjectsByType(objects, islandData));
							await Promise.all(waveAnimations);
							waveIslands.clear();
						}

						// Reset state for next phase
						currentIslandId = null;
						currentWave = null;
						// Don't add the empty group to the scene
					} else if (metadata.type === 'cutout' || metadata.type === 'debug') {
						// Before processing cutouts, finish animating all island geometry
						// First, add current island to wave if it has objects
						if (currentIslandId && currentIslandObjects.length > 0) {
							const prevIslandData = islandGroups.get(currentIslandId);
							if (prevIslandData) {
								waveIslands.set(currentIslandId, { objects: [...currentIslandObjects], islandData: prevIslandData });
							}
							currentIslandObjects = [];
						}
						// Animate all remaining wave islands in parallel
						if (waveIslands.size > 0) {
							const waveAnimations = Array.from(waveIslands.values()).map(({ objects, islandData }) => this.animateIslandObjectsByType(objects, islandData));
							await Promise.all(waveAnimations);
							waveIslands.clear();
						}
						currentIslandId = null;
						currentWave = null;

						// Cutouts are added immediately (instant animation in registry)
						objectHolder.add(object);
						if (this.#sceneTransitionManager) {
							await this.#sceneTransitionManager.animateAdditionByType(sceneObject, undefined, true);
						}
						this.emit('object:added', { id: metadata.id, type: metadata.type });
					} else if (metadata.type === 'appliance') {
						// Before processing appliances, finish animating all pending island geometry
						// First, add current island to wave if it has objects
						if (currentIslandId && currentIslandObjects.length > 0) {
							const prevIslandData = islandGroups.get(currentIslandId);
							if (prevIslandData) {
								waveIslands.set(currentIslandId, { objects: [...currentIslandObjects], islandData: prevIslandData });
							}
							currentIslandObjects = [];
						}
						// Animate all remaining wave islands in parallel
						if (waveIslands.size > 0) {
							const waveAnimations = Array.from(waveIslands.values()).map(({ objects, islandData }) => this.animateIslandObjectsByType(objects, islandData));
							await Promise.all(waveAnimations);
							waveIslands.clear();
						}
						currentIslandId = null;
						currentWave = null;

						// Appliances are animated individually with their own island data
						prepareMaterialsForFade(object);
						setObjectOpacity(object, 0);

						// Add to scene only if not already parented
						if (!object.parent) {
							objectHolder.add(object);
						}

						// Animate appliance immediately with its own island's data (or worldCenter)
						if (this.#sceneTransitionManager) {
							// For appliances, use current presentation point (world coords) instead of island center
							const currentPresentationPoint = this.getCurrentPresentationPoint();
							const useSceneCenter = currentPresentationPoint !== null;
							const animationCenter = useSceneCenter
								? { x: currentPresentationPoint!.x, y: currentPresentationPoint!.y, z: currentPresentationPoint!.z }
								: islandGroups.get(metadata.islandId ?? 'unknown')?.center;

							// Get camera position for "face camera" initial rotation
							const camPos = this.#cameraManager?.camera?.position;
							const cameraPosition = camPos ? { x: camPos.x, y: camPos.y, z: camPos.z } : undefined;

							applianceAnimations.push(
									this.#sceneTransitionManager.animateAdditionByType(sceneObject, animationCenter, true, useSceneCenter, cameraPosition)
								);
						}
						this.emit('object:added', { id: metadata.id, type: metadata.type });
					} else {
						// Panel, corner, countertop - collect for batched animation per island
						// Set invisible immediately to prevent flash while waiting for animation
						// prepareMaterialsForFade stores ORIGINAL state before we set opacity=0
						prepareMaterialsForFade(object);
						setObjectOpacity(object, 0);

						// Add to scene only if not already parented (e.g., panels are children of island groups)
						if (!object.parent) {
							objectHolder.add(object);
						}

						currentIslandObjects.push(sceneObject);
						islandGroups.get(metadata.islandId ?? currentIslandId ?? 'unknown')?.objects.push(sceneObject);
					}

					result = await generator.next();
				}

				// Finish any remaining island objects
				// This handles cases where no cutout/appliance triggered the animation
				if (currentIslandId && currentIslandObjects.length > 0) {
					const prevIslandData = islandGroups.get(currentIslandId);
					if (prevIslandData) {
						waveIslands.set(currentIslandId, { objects: [...currentIslandObjects], islandData: prevIslandData });
					}
				}
				// Animate all remaining wave islands in parallel
				if (waveIslands.size > 0) {
					const waveAnimations = Array.from(waveIslands.values()).map(({ objects, islandData }) => this.animateIslandObjectsByType(objects, islandData));
					await Promise.all(waveAnimations);
					waveIslands.clear();
				}

				// Wait for all in-flight appliance slide-ins (fired in parallel above)
				if (applianceAnimations.length > 0) {
					await Promise.all(applianceAnimations);
				}

				// Generator return value is the final composition
				finalComposition = result.value as SceneComposition;

				// Apply camera and bounds from final composition (only if not already applied)
				// Skip if: camera was set via setup object OR camera was pre-positioned before build started
				const shouldApplyCamera = finalComposition && !cameraAlreadyApplied && !cameraPrePositioned;
				if (shouldApplyCamera) {
					const animConfig = this.#config.progressiveAnimation ?? {};
					const cameraTransitionDuration = animConfig.cameraTransitionDuration ?? 800;
					await this.applyCompositionSettingsAnimated(finalComposition, cameraTransitionDuration);
				}
			} catch (error) {
				const msg = error instanceof Error ? error.message : String(error);
				console.error(`Failed to build scene progressively with types from ${source.builder}:`, error);
				this.#lastBuildError = msg;
				this.emit('render:error', { error: msg });
			}
		}

		// Snapshot scene state for future incremental updates
		if (this.#sceneDiffManager && this.#sceneManager?.objectHolder) {
			this.#sceneDiffManager.snapshotScene(this.#sceneManager.objectHolder);
			this.#hasInitialScene = true;
		}

		// Reset build flag before processing pending updates
		this.#isBuildingScene = false;

		// Ensure environment map is set for metallic reflections
		this.ensureEnvironmentMapForReflections();

		// Debug: Check final opacity state of objects
		this.debugCheckObjectOpacity(objectHolder);

		this.emit('render:complete', { isRebuild: true });

		// Auto-zoom: slide camera from source to fit position, or true up the
		// framing the scene was already built inside.
		if (this.#cameraDelegate?.isAutoZoomEnabled && this.#cameraDelegate?.lastSceneBounds) {
			if (this.#cameraDelegate.autoZoomStyle === 'settle') {
				this.#cameraDelegate.settleAutoZoom();
			} else {
				await this.performAutoZoom(true, 2000);
			}
		}

		// Ensure at least one frame renders after progressive-with-types composition settles
		this.requestRender();

		// Process any pending update that was queued during this progressive compose
		await this.processPendingUpdate();
	}

	/**
	 * Animate objects for a single island using type-based animation order.
	 * @param objects - Objects to animate
	 * @param islandData - Island center and objects
	 * @param isInitialRender - If true, uses 'initialize' transition instead of 'add'
	 */
	private animateIslandObjectsByType (
		objects: import('../types').SceneObject[],
		islandData?: { center: { x: number; y: number; z: number }; objects: import('../types').SceneObject[] },
		isInitialRender = true
	): Promise<void> {
		return animateIslandObjectsByTypeCfn({
			objects,
			islandData,
			isInitialRender,
			sceneTransitionManager: this.#sceneTransitionManager,
			cameraManager: this.#cameraManager,
			getCurrentPresentationPoint: () => this.getCurrentPresentationPoint(),
			emit: (type, data) => this.emit(type, data)
		});
	}

	private applyCompositionSettings (composition: SceneComposition): void {
		applyCompositionSettingsCfn({
			composition,
			cameraManager: this.#cameraManager,
			cameraDelegate: this.#cameraDelegate,
			highlightManager: this.#highlightManager
		});
	}

	private applyCompositionSettingsAnimated (composition: SceneComposition, duration: number): void {
		applyCompositionSettingsAnimatedCfn({
			composition,
			duration,
			cameraManager: this.#cameraManager,
			cameraDelegate: this.#cameraDelegate,
			highlightManager: this.#highlightManager
		});
	}

	// Progressive rendering material helpers
	#progressiveMaterialStates = new Map<Material, { transparent: boolean; opacity: number }>();

	private prepareMaterialsForFadeProgressive (object: Object3D): void {
		object.traverse((child) => {
			if (child instanceof Mesh && child.material) {
				const materials = Array.isArray(child.material) ? child.material : [child.material];
				for (const material of materials) {
					if (!this.#progressiveMaterialStates.has(material)) {
						this.#progressiveMaterialStates.set(material, {
							transparent: material.transparent,
							opacity: material.opacity
						});
					}
					material.transparent = true;
				}
			}
		});
	}

	private setObjectOpacityProgressive (object: Object3D, opacity: number): void {
		object.traverse((child) => {
			if (child instanceof Mesh && child.material) {
				const materials = Array.isArray(child.material) ? child.material : [child.material];
				for (const material of materials) {
					if (material.name === 'Image-arch') {
						material.opacity = 0;
					} else {
						material.opacity = opacity;
					}
				}
			}
		});
	}

	private restoreMaterialsProgressive (object: Object3D): void {
		object.traverse((child) => {
			if (child instanceof Mesh && child.material) {
				const materials = Array.isArray(child.material) ? child.material : [child.material];
				for (const material of materials) {
					const state = this.#progressiveMaterialStates.get(material);
					if (state) {
						material.transparent = state.transparent;
						material.opacity = state.opacity;
						this.#progressiveMaterialStates.delete(material);
					}
				}
			}
		});
	}

	/**
	 * Update scene incrementally with animated transitions.
	 *
	 * This method diffs the current scene against the new composition and:
	 * 1. Fades out removed objects
	 * 2. Animates moved objects to their new positions
	 * 3. Fades in added objects
	 *
	 * Falls back to full rebuild if:
	 * - No initial scene exists
	 * - Structural changes detected (islands, panels)
	 * - Error occurs during diff
	 *
	 * @param sources - Data sources to update from
	 * @param options - Options for the update
	 */
	async updateScene (
		sources: DataSource[],
		options?: {
			/** Skip animations and do immediate update */
			immediate?: boolean;
			/** Force full rebuild instead of incremental */
			forceRebuild?: boolean;
			/** Skip structural change detection — use when structural changes are
			 *  expected and should be handled by the transition pipeline (e.g.,
			 *  cutout geometry shifting after appliance redistribution) */
			skipStructuralCheck?: boolean;
		}
	): Promise<void> {
		// console.log('ThreeSceneEngine.updateScene: Called', {
		// hasInitialScene: this.#hasInitialScene,
		// forceRebuild: options?.forceRebuild,
		// sourceCount: sources.length,
		// isTransitioning: this.#sceneTransitionManager?.isTransitioning
		// });

		// If no initial scene, do full compose
		if (!this.#hasInitialScene || options?.forceRebuild) {
			// console.log('ThreeSceneEngine.updateScene: No initial scene or forced rebuild, doing full compose', {
			// hasInitialScene: this.#hasInitialScene,
			// forceRebuild: options?.forceRebuild
			// });
			await this.rebuild(sources);
			return;
		}

		// If transition in progress, queue the update for later
		if (this.#sceneTransitionManager?.isTransitioning) {
			// console.log('ThreeSceneEngine.updateScene: Transition in progress, queueing update');
			this.#pendingUpdate = { sources, options };
			return;
		}

		// Store sources for rebuild
		this.#lastDataSources = sources;

		this.emit('render:start', { isRebuild: false });

		// Ensure the render loop is running during scene transitions
		this.#sceneManager?.renderScheduler?.startLoop();

		try {
			// Build new compositions
			const newCompositions: SceneComposition[] = [];

			for (const source of sources) {
				const builder = this.#builders.get(source.builder);
				if (!builder) {
					console.warn(`No builder registered for: ${source.builder}`);
					continue;
				}

				const composition = await builder.build(source.data);
				newCompositions.push(composition);
			}

			// Merge all compositions into one
			const mergedComposition = mergeCompositionsCfn(newCompositions);

			// Compute diff
			if (this.#sceneDiffManager) {
				const diff = this.#sceneDiffManager.computeDiff(mergedComposition);

				// console.log('ThreeSceneEngine.updateScene: Diff computed', {
				// added: diff.added.length,
				// removed: diff.removed.length,
				// moved: diff.moved.length,
				// unchanged: diff.unchanged.length,
				// addedIds: diff.added.map(o => ({ id: o.metadata.id, type: o.metadata.type })),
				// removedIds: diff.removed.map(o => ({ id: o.id, type: o.type })),
				// movedIds: diff.moved.map(m => ({ id: m.metadata.id, type: m.metadata.type }))
				// });

				// Check if there are any changes
				const hasChanges = diff.added.length > 0 || diff.removed.length > 0 || diff.moved.length > 0;

				if (!hasChanges) {
					// console.log('ThreeSceneEngine.updateScene: No changes detected');
					this.emit('render:complete', { isRebuild: false });
					return;
				}

				// Check if changes are only appliance-related (can be animated)
				// Structural changes (islands, panels, countertops) require full rebuild
				// unless skipStructuralCheck is set (e.g., cutout geometry shifting
				// after appliance redistribution — handled by transition pipeline)
				if (!options?.skipStructuralCheck) {
					const hasStructuralChanges = hasStructuralChangesCfn(diff);

					if (hasStructuralChanges) {
						// Structural changes should be handled by per-island rebuild before
						// updateScene is called. Log a warning but still proceed with the
						// diff-based transition as a safety net.
						console.warn('[ThreeSceneEngine] updateScene: unexpected structural changes detected — should have been handled by rebuildIsland()');
					}
				}

				// console.log('ThreeSceneEngine.updateScene: Appliance-only changes, doing animated transition', {
				// added: diff.added.map(o => o.metadata.id),
				// removed: diff.removed.map(o => o.id),
				// moved: diff.moved.map(m => m.metadata.id)
				// });

				// Execute animated transition or immediate update
				if (options?.immediate || !this.#sceneTransitionManager) {
					// Immediate update without animations
					const objectHolder = this.#sceneManager?.objectHolder;
					if (objectHolder) applyDiffImmediateCfn(diff, objectHolder);
				} else {
					// Animated transition
					await this.#sceneTransitionManager.executeTransition(diff);
				}

				// Update snapshot for next diff
				if (this.#sceneManager?.objectHolder) {
					this.#sceneDiffManager.snapshotScene(this.#sceneManager.objectHolder);
				}
			}
		} catch (error) {
			console.error('ThreeSceneEngine.updateScene: Error during update:', error);
			// Fall back to full rebuild
			await this.rebuild(sources);
		}

		this.emit('render:complete', { isRebuild: false });

		// Ensure at least one frame renders after the incremental update
		this.requestRender();

		// Process any pending update that was queued during transition
		await this.processPendingUpdate();
	}

	/**
	 * Process any pending update that was queued while a transition was in progress
	 */
	private async processPendingUpdate (): Promise<void> {
		if (this.#pendingUpdate) {
			const { sources, options } = this.#pendingUpdate;
			this.#pendingUpdate = null;
				await this.updateScene(sources, options);
		}
	}

	/**
	 * Regenerate one island's countertop/surround cutouts from the domain with
	 * per-appliance adjustments (holes left out, holes shifted), without
	 * rebuilding anything else. Used by the drag-and-drop preview; calling it
	 * with no adjustment restores the domain's holes. Requests for the same
	 * island are serialised and a request made while one runs replaces any
	 * request still waiting, so a burst of drag updates costs at most two CSG passes.
	 */
	async previewIslandCutouts (islandId: string, adjust?: CutoutAdjust): Promise<void> {
		if (this.#cutoutPreviewRunning.has(islandId)) {
			this.#cutoutPreviewPending.set(islandId, adjust);
			return this.#cutoutPreviewRunning.get(islandId);
		}
		const run = (async (): Promise<void> => {
			try {
				await this.#regenerateIslandCutoutsNow(islandId, adjust);
			} catch (error) {
				console.warn('[ThreeSceneEngine] previewIslandCutouts failed', { islandId, error });
			} finally {
				this.#cutoutPreviewRunning.delete(islandId);
				if (this.#cutoutPreviewPending.has(islandId)) {
					const next = this.#cutoutPreviewPending.get(islandId);
					this.#cutoutPreviewPending.delete(islandId);
					void this.previewIslandCutouts(islandId, next);
				}
			}
		})();
		this.#cutoutPreviewRunning.set(islandId, run);
		return run;
	}

	/**
	 * Drop every queued cutout preview and resolve once the running ones have
	 * finished. A drop's rebuild must start with no preview CSG in flight: the
	 * cutout manager's working list is shared, so a preview finishing after the
	 * rebuild would re-cut the new island with the drag's stale holes.
	 */
	async cancelPendingCutoutPreviews (): Promise<void> {
		this.#cutoutPreviewPending.clear();
		await Promise.all(Array.from(this.#cutoutPreviewRunning.values()).map((run) => run.catch(() => undefined)));
	}

	async #regenerateIslandCutoutsNow (islandId: string, adjust?: CutoutAdjust): Promise<void> {
		const objectHolder = this.#sceneManager?.objectHolder;
		const islandGroup = objectHolder?.children.find((child) => child.userData?.type === 'island' && child.userData?.id === islandId) as Group | undefined;
		const kitchenBuilder = this.#builders.get('kitchen') as (KitchenSceneBuilder & { kitchenDataManagerAccess?: { init: (k: unknown) => void } }) | undefined;
		if (!objectHolder || !islandGroup || !kitchenBuilder?.regenerateCutoutsForIsland) return;

		// cutouts are read from the live domain, like every other regeneration
		const kitchenSource = this.#lastDataSources?.find((s) => s.builder === 'kitchen');
		if (kitchenBuilder.kitchenDataManagerAccess && kitchenSource?.data) {
			kitchenBuilder.kitchenDataManagerAccess.init((kitchenSource.data as { kitchen: unknown }).kitchen);
		}

		const debugGroup = await kitchenBuilder.regenerateCutoutsForIsland(islandGroup, adjust);
		const existingDebug = objectHolder.getObjectByName('cutout-debug-group');
		if (existingDebug) existingDebug.parent?.remove(existingDebug);
		if (debugGroup) objectHolder.add(debugGroup);
		this.requestRender();
	}

	private async regenerateCutoutsForCurrentScene (): Promise<void> {
		// Re-init kitchen data from the live domain model so cutout
		// regeneration reflects the current state (e.g. after appliance removal).
		const kitchenSource = this.#lastDataSources?.find((s) => s.builder === 'kitchen');
		const kitchenBuilder = this.#builders.get('kitchen') as { kitchenDataManagerAccess?: { init: (k: unknown) => void } } | undefined;
		if (kitchenBuilder?.kitchenDataManagerAccess && kitchenSource?.data) {
			kitchenBuilder.kitchenDataManagerAccess.init((kitchenSource.data as { kitchen: unknown }).kitchen);
		}

		await regenerateCutoutsForCurrentSceneCfn({
			sceneManager: this.#sceneManager,
			builders: this.#builders
		});
	}

	/**
	 * Apply a scene composition
	 *
	 * Uses calculated camera position based on kitchen geometry for optimal viewing angle.
	 * Camera is positioned at 5'8" eye height, facing the kitchen center.
	 */
	async applyComposition (composition: SceneComposition, layer?: string): Promise<void> {
		await applyCompositionCfn({
			composition,
			layer,
			sceneManager: this.#sceneManager,
			cameraManager: this.#cameraManager,
			cameraDelegate: this.#cameraDelegate,
			highlightManager: this.#highlightManager,
			geoLightingManager: this.#geoLightingManager,
			debugManager: this.#debugManager,
			debugMode: this.#debugMode,
			textureDelegate: this.#textureDelegate,
			removeFoundationFloor: () => this.removeFoundationFloor()
		});
	}

	private ensureEnvironmentMapForReflections (): void {
		this.#textureDelegate?.ensureEnvironmentMapForReflections();
	}

	// ========== Object Management ==========

	addObject (object: Object3D, metadata?: ObjectMetadata): void {
		this.#objectDelegate?.addObject(object, metadata);
	}

	removeObject (id: string, animate = true): void {
		this.#objectDelegate?.removeObject(id, animate);
	}

	removeObjectAnimated (id: string): Promise<void> {
		return this.#objectDelegate?.removeObjectAnimated(id) ?? Promise.resolve();
	}

	applyTargetedChanges (changes: import('../../../controller/types/tool.types').SceneChangeHints): Promise<void> {
		const apply = this.#objectDelegate?.applyTargetedChanges(changes) ?? Promise.resolve();
		// Non-animated mutations (cutout regeneration, instant swaps) can land
		// after the last animation finishes and the render loop has already
		// self-terminated — repaint once so the final state always reaches the
		// canvas.
		return apply.finally(() => { this.requestRender(); });
	}

	removeIslandGroupAnimated (islandId: string): Promise<void> {
		return this.#objectDelegate?.removeIslandGroupAnimated(islandId) ?? Promise.resolve();
	}

	findIslandById (islandId: string): import('three').Group | null {
		return this.#objectDelegate?.findIslandById(islandId) ?? null;
	}

	animateObjectPosition (id: string, newPosition: { x: number; y: number; z: number }): Promise<void> {
		return this.#objectDelegate?.animateObjectPosition(id, newPosition) ?? Promise.resolve();
	}

	animateIslandPosition (islandId: string, newPosition: { x: number; y: number; z: number }): Promise<void> {
		return this.#objectDelegate?.animateIslandPosition(islandId, newPosition) ?? Promise.resolve();
	}

	addObjectsToScene (objects: Array<{ id: string; islandId?: string; sku?: string }>): Promise<void> {
		return this.#objectDelegate?.addObjectsToScene(objects) ?? Promise.resolve();
	}

	updateMaterialByType (materialType: string, key: string): void {
		this.#objectDelegate?.updateMaterialByType(materialType, key);
	}

	updateSceneSnapshot (): void {
		this.#objectDelegate?.updateSceneSnapshot();
	}

	updateObject (id: string, transform: Transform): void {
		this.#objectDelegate?.updateObject(id, transform);
	}

	getObject (id: string): Object3D | null {
		return this.#objectDelegate?.getObject(id) ?? null;
	}

	clearObjects (): void {
		this.#objectDelegate?.clearObjects();
	}

	// ========== Texture Loading ==========

	/**
	 * Load textures
	 */
	async loadTextures (
		textureInfos: TextureInfo[],
		callbacks?: TextureLoadCallbacks
	): Promise<void> {
		await this.#textureManager?.loadTextures(textureInfos, callbacks);
	}

	// ========== Model Loading ==========

	/**
	 * Load models
	 */
	async loadModels (models: ModelLoadOptions[]): Promise<Object3D[]> {
		return this.#modelManager?.loadModels(models) ?? [];
	}

	// ========== Cutouts ==========

	/**
	 * Add a cutout
	 */
	addCutout (cutout: CutoutInfo): void {
		this.#cutoutManager?.addCutout(cutout);
	}

	// ========== Camera ==========

	/**
	 * Set camera zoom
	 */
	setZoom (zoom: number): void {
		this.#cameraManager?.setZoom(zoom);
	}

	/**
	 * Rotate camera to angle
	 */
	async rotateCamera (angle: number, elevation?: number, duration?: number): Promise<void> {
		await this.#cameraManager?.rotateToAngle(angle, elevation, duration);
	}

	// ========== Scene Rebuild ==========

	/**
	 * Rebuild the scene from registered builders
	 *
	 * @param sources - Data sources to rebuild (uses previous sources if not provided)
	 */
	async rebuild (sources?: DataSource[]): Promise<void> {
		const sourcesToUse = sources || this.#lastDataSources;

		if (!sourcesToUse || sourcesToUse.length === 0) {
			console.warn('ThreeSceneEngine.rebuild: No sources to rebuild from');
			return;
		}

		// Clear existing objects
		this.clearObjects();

		// Clear highlight state
		this.clearHighlight();

		// Rebuild from sources (compose will emit render:start and render:complete)
		await this.compose(sourcesToUse);
	}

	/**
	 * Build a single island from the kitchen builder and return it.
	 * Used by the object delegate for per-island rebuilds.
	 */
	async #rebuildIslandGroup (islandId: string): Promise<import('three').Group | null> {
		const kitchenBuilder = this.#builders.get('kitchen') as { buildSingleIsland?: (islandId: string, input: unknown) => Promise<import('three').Group | null> } | undefined;
		if (!kitchenBuilder?.buildSingleIsland) {
			console.warn('[ThreeSceneEngine] #rebuildIslandGroup: kitchen builder has no buildSingleIsland method');
			return null;
		}

		const kitchenSource = this.#lastDataSources?.find((s) => s.builder === 'kitchen');
		if (!kitchenSource?.data) {
			console.warn('[ThreeSceneEngine] #rebuildIslandGroup: no kitchen data source');
			return null;
		}

		return kitchenBuilder.buildSingleIsland(islandId, kitchenSource.data);
	}

	/**
	 * Recalculate bounding box from current scene island objects and smoothly
	 * reposition the camera to frame the new layout. Uses the same zoomToFit
	 * calculation as initial scene setup with smooth transition enabled.
	 */
	#repositionCameraForCurrentScene (): void {
		const objectHolder = this.#sceneManager?.objectHolder;
		const cameraManager = this.#cameraManager;
		if (!objectHolder || !cameraManager) return;

		const box = new Box3();
		let hasIslands = false;

		for (const child of objectHolder.children) {
			if (child.userData?.type === 'island') {
				box.expandByObject(child);
				hasIslands = true;
			}
		}

		if (!hasIslands) return;

		const min = box.min;
		const max = box.max;

		cameraManager.zoomToFit(min, max, true);
	}

	/**
	 * Full camera reset: recalculate scene bounds, camera position, target,
	 * auto-zoom position, presentation point, and animate to the new view.
	 * Mirrors the initial render camera setup flow.
	 *
	 * @param kitchenDebugData - Kitchen geometry data for camera positioning.
	 *   When provided, recalculates camera vector, target, and arc from kitchen shape.
	 *   When omitted, falls back to bounds-only repositioning.
	 */
	async resetView (kitchenDebugData?: KitchenDebugData): Promise<void> {
		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder || !this.#cameraManager) return;

		// Resolve kitchen data: explicit arg > stored > none
		const debugData = kitchenDebugData ?? this.#lastKitchenDebugData;

		// 1. Recalculate bounding box from current scene island objects
		const box = new Box3();
		let hasIslands = false;
		for (const child of objectHolder.children) {
			if (child.userData?.type === 'island') {
				box.expandByObject(child);
				hasIslands = true;
			}
		}
		if (!hasIslands) return;

		// 2. Update lastSceneBounds and highlight circle on camera delegate
		const bounds = {
			min: { x: box.min.x, y: box.min.y, z: box.min.z },
			max: { x: box.max.x, y: box.max.y, z: box.max.z }
		};
		this.#cameraDelegate?.applyBoundsAndHighlight(bounds, this.#highlightManager);

		// 3. Recalculate camera state from kitchen geometry WITHOUT moving the camera
		if (debugData) {
			this.#cameraDelegate?.setSceneCenterFromKitchenData(debugData, true);

			// 4. Update debug visualizations (bounding box, circles, spheres)
			if (this.#debugDelegate) {
				this.#debugDelegate.setKitchenDebugData(debugData);
			}
		}

		// 5. Recalculate auto-zoom position, arc path, presentation point,
		//    distance constraints, then smoothly animate from current position
		if (this.#cameraDelegate?.lastSceneBounds) {
			await this.#cameraDelegate.resetAutoZoom();
		}
	}

	/**
	 * Dissolve existing scene objects with animation, then rebuild with new sources.
	 * Used when replacing one blueprint with another for a smooth visual transition.
	 *
	 * @param sources - New data sources to build from
	 * @param useProgressiveRender - Whether to use progressive rendering for the new scene
	 * @param mode - Transition mode: 'dissolve' (sequential), 'crossfade' (overlap), or 'instant'
	 */
	async dissolveAndRebuild (
		sources: DataSource[],
		useProgressiveRender = false,
		mode: 'dissolve' | 'crossfade' | 'instant' = 'dissolve'
	): Promise<void> {
		this.#buildGeneration++;
		// Instant mode: just clear and rebuild (same as rebuild())
		if (mode === 'instant') {
			await this.rebuild(sources);
			return;
		}

		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder) {
			await this.rebuild(sources);
			return;
		}

		// Collect snapshots of all existing objects for animated removal
		const snapshots: ObjectSnapshot[] = [];
		for (const child of [...objectHolder.children]) {
			snapshots.push({
				id: child.userData?.id || child.uuid,
				type: child.userData?.type || 'unknown',
				object: child,
				position: child.position.clone(),
				rotation: {
					x: child.rotation.x,
					y: child.rotation.y,
					z: child.rotation.z
				}
			});
		}

		if (mode === 'crossfade') {
			// Crossfade: start dissolve and build simultaneously
			const dissolvePromise = snapshots.length > 0 && this.#sceneTransitionManager
				? this.#sceneTransitionManager.dissolveAllByType(snapshots).then(() => {
					// Remove dissolved objects after animation
					for (const snap of snapshots) {
						if (snap.object.parent) snap.object.parent.remove(snap.object);
					}
				})
				: Promise.resolve();

			// Reset state for fresh build
			this.#hasInitialScene = false;
			this.#sceneDiffManager?.clear();
			this.clearHighlight();

			// Start building new scene in parallel with dissolve
			const buildPromise = useProgressiveRender
				? this.composeProgressiveWithTypes(sources)
				: this.compose(sources);

			await Promise.all([dissolvePromise, buildPromise]);
		} else {
			// Dissolve mode (default): dissolve first, then build
			if (snapshots.length > 0 && this.#sceneTransitionManager) {
				await this.#sceneTransitionManager.dissolveAllByType(snapshots);
			}

			// Clear all objects after animation completes
			this.clearObjects();
			this.clearHighlight();

			// Reset state for fresh build
			this.#hasInitialScene = false;
			this.#sceneDiffManager?.clear();

			// Build new scene with animations
			if (useProgressiveRender) {
				await this.composeProgressiveWithTypes(sources);
			} else {
				await this.compose(sources);
			}
		}
	}

	/**
	 * Rebuild a specific layer/builder
	 */
	async rebuildLayer (builderName: string, data: unknown): Promise<void> {
		const builder = this.#builders.get(builderName);
		if (!builder) {
			console.warn('ThreeSceneEngine.rebuildLayer: Builder not found:', builderName);
			return;
		}

		// Clear existing objects from this layer
		this.clearLayer(builderName);

		// Rebuild
		const composition = await builder.build(data);
		await this.applyComposition(composition, builderName);
	}

	/**
	 * Clear objects from a specific layer
	 */
	clearLayer (layerName: string): void {
		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder) return;

		const layerGroup = objectHolder.getObjectByName(layerName);
		if (layerGroup) {
			// Collect materials and geometries for deferred disposal
			const toDispose: { geometry?: any; materials?: any[] }[] = [];
			layerGroup.traverse((child: any) => {
				const item: { geometry?: any; materials?: any[] } = {};
				if (child.geometry) item.geometry = child.geometry;
				if (child.material) {
					item.materials = Array.isArray(child.material) ? child.material : [child.material];
				}
				if (item.geometry || item.materials) toDispose.push(item);
			});
			objectHolder.remove(layerGroup);
			// Defer disposal to avoid WebGL errors during active rendering
			requestAnimationFrame(() => {
				for (const item of toDispose) {
					item.geometry?.dispose();
					item.materials?.forEach((m) => m.dispose());
				}
			});
		}
	}

	// ========== Highlighting ==========

	/**
	 * Highlight an object by ID
	 *
	 * @param objectId - The ID of the object to highlight (from userData.id)
	 * @param addHighlight - If true, adds highlight without removing existing ones
	 */
	highlightObject (objectId: string, addHighlight = false): void {
		if (!this.#highlightManager) {
			console.warn('ThreeSceneEngine.highlightObject: Highlight manager not available');
			return;
		}

		// Clear highlight if empty ID
		if (!objectId || objectId === '') {
			this.clearHighlight();
			return;
		}

		// Find the object by ID
		const object = this.findObjectById(objectId);
		if (!object) {
			console.warn('ThreeSceneEngine.highlightObject: Object not found:', objectId);
			return;
		}

		// Highlight the object
		this.#highlightManager.highlight(objectId, object, addHighlight);
		this.requestRender();
	}

	/**
	 * Clear the current highlight
	 */
	clearHighlight (objectId?: string): void {
		this.#highlightManager?.clearHighlight(objectId);
		this.requestRender();
	}

	/** Highlight an object unless it already is — never the toggle-off that `highlightObject` does for a repeat id. */
	ensureHighlight (objectId: string): void {
		if (!this.#highlightManager) return;
		if (this.#highlightManager.currentHighlightId === String(objectId)) return;
		this.highlightObject(objectId, false);
	}

	/** Re-aim a highlight at its object's current position (call per frame while it moves). */
	followHighlight (objectId: string): void {
		if (this.#highlightManager?.followHighlight(objectId)) this.requestRender();
	}

	/**
	 * Check if an object is currently highlighted
	 */
	isHighlighted (objectId: string): boolean {
		return this.#highlightManager?.hasHighlight(objectId) ?? false;
	}

	/**
	 * Get all highlighted object IDs
	 */
	getHighlightedIds (): string[] {
		return this.#highlightManager?.getAllHighlightIds() ?? [];
	}

	/**
	 * Set highlight circle configuration (needed for spotlight positioning)
	 */
	setHighlightCircle (config: { center: { x: number; y: number; z: number }; radius: number }): void {
		if (!this.#highlightManager) return;

		// HighlightCircleConfig uses centerX, centerZ, y format
		this.#highlightManager.setCircle({
			centerX: config.center.x,
			centerZ: config.center.z,
			y: config.center.y,
			radius: config.radius
		});
	}

	/**
	 * Find an object by ID in the scene
	 */
	findObjectById (objectId: string): Object3D | null {
		return this.#objectDelegate?.findObjectById(objectId) ?? null;
	}

	// ========== Texture Updates ==========

	async changeSurroundTexture (textureKey: string): Promise<boolean> {
		const needsRebuild = await this.#textureDelegate?.changeSurroundTexture(textureKey) ?? false;
		if (!needsRebuild) {
			this.requestRender();
		}
		return needsRebuild;
	}

	async changeCountertopTexture (textureKey: string): Promise<void> {
		await this.#textureDelegate?.changeCountertopTexture(textureKey);
		this.requestRender();
	}

	async changeFloorTexture (textureKey: string): Promise<void> {
		await this.#textureDelegate?.changeFloorTexture(textureKey);
		this.requestRender();
	}

	async changeSkyTexture (textureKey: string): Promise<void> {
		await this.#textureDelegate?.changeSkyTexture(textureKey);
		this.requestRender();
	}

	async setCustomSkyTexture (dataUrl: string): Promise<void> {
		await this.#textureDelegate?.setCustomSkyTexture(dataUrl);
		this.requestRender();
	}

	async upgradeSkyTextureWithFade (
		textureKey: string,
		targetTextureFilename: string,
		fadeDuration = 300
	): Promise<void> {
		return this.#textureDelegate?.upgradeSkyTextureWithFade(textureKey, targetTextureFilename, fadeDuration);
	}

	async processPendingSkyUpgrades (
		pendingUpgrades: Array<{ key: string; targetTexture: string }>,
		currentSkyKey: string
	): Promise<void> {
		return this.#textureDelegate?.processPendingSkyUpgrades(pendingUpgrades, currentSkyKey);
	}

	setSceneEnvironment (textureKey: string): void {
		this.#textureDelegate?.setSceneEnvironment(textureKey);
		this.requestRender();
	}

	clearSkyTexture (): void {
		this.#textureDelegate?.clearSkyTexture();
		this.requestRender();
	}

	clearFloorTexture (): void {
		this.#textureDelegate?.clearFloorTexture();
		this.requestRender();
	}

	// ========== Directional Light Fading (Highlight Support) ==========

	/** Store original directional light intensity for restoring */
	#directionalLightOriginalIntensity = 2.0;
	#directionalLightFadeAnimationId = 'directional-light-fade';

	/**
	 * Fade directional light in or out
	 * Used when highlights are added/removed to avoid competing light sources
	 *
	 * @param fadeIn - true to fade in (restore), false to fade out (dim)
	 */
	private fadeDirectionalLight (fadeIn: boolean): void {
		const directionalLight = this.#lightingManager?.directionalLight;
		if (!directionalLight || !this.#animationManager) return;

		// Store original intensity when fading out
		if (!fadeIn && directionalLight.intensity > 0) {
			this.#directionalLightOriginalIntensity = directionalLight.intensity;
		}

		// Stop any existing fade animation
		this.#animationManager.stop(this.#directionalLightFadeAnimationId);

		const startIntensity = directionalLight.intensity;
		const targetIntensity = fadeIn ? this.#directionalLightOriginalIntensity : 0;
		const duration = 1000; // 1 second fade

		// console.log(`ThreeSceneEngine: Fading directional light ${fadeIn ? 'in' : 'out'}`, {
		// from: startIntensity,
		// to: targetIntensity
		// });

		this.#animationManager.start({
			id: this.#directionalLightFadeAnimationId,
			duration,
			onUpdate: (progress) => {
				// Use eased progress for smooth transition
				const easedProgress = progress * progress * (3 - 2 * progress); // smoothstep
				directionalLight.intensity = startIntensity + (targetIntensity - startIntensity) * easedProgress;
			},
			onComplete: () => {
				directionalLight.intensity = targetIntensity;
				if (!fadeIn) {
					directionalLight.visible = false;
				}
			}
		});

		// Make light visible immediately when fading in
		if (fadeIn) {
			directionalLight.visible = true;
		}
	}

	// ========== Geographic Lighting ==========

	/**
	 * Enable geographic lighting with a location
	 * This enables realistic sun/moon positioning based on geographic coordinates
	 *
	 * @param location - Geographic coordinates (latitude, longitude)
	 */
	enableGeoLighting (location: GeoLocation): void {
		if (!this.#geoLightingManager) {
			console.warn('ThreeSceneEngine.enableGeoLighting: GeoLightingManager not initialized');
			return;
		}

		this.#geoLightingManager.enable(location);
		this.emit('geo_lighting:changed', this.#geoLightingManager.state);
		this.requestRender();
	}

	/**
	 * Disable geographic lighting and restore default lighting
	 */
	disableGeoLighting (): void {
		if (!this.#geoLightingManager) return;

		this.#geoLightingManager.disable();
		this.emit('geo_lighting:changed', { enabled: false });
		this.requestRender();
	}

	/**
	 * Check if geographic lighting is enabled
	 */
	get isGeoLightingEnabled (): boolean {
		return this.#geoLightingManager?.isEnabled ?? false;
	}

	/**
	 * Get current geographic lighting state
	 */
	get geoLightingState (): GeoLightingState | null {
		return this.#geoLightingManager?.state ?? null;
	}

	/**
	 * Set geographic location for sun/moon calculations
	 *
	 * @param location - Geographic coordinates
	 */
	setGeoLocation (location: GeoLocation): void {
		if (!this.#geoLightingManager) return;

		this.#geoLightingManager.setLocation(location);
		if (this.#geoLightingManager.isEnabled) {
			this.emit('geo_lighting:changed', this.#geoLightingManager.state);
			this.requestRender();
		}
	}

	/**
	 * Set time for sun/moon position calculation
	 *
	 * @param time - Date object, time preset ('sunrise', 'morning', etc.), or 'realtime'
	 */
	setGeoTime (time: Date | TimePreset | 'realtime'): void {
		if (!this.#geoLightingManager) return;

		this.#geoLightingManager.setTime(time);
		if (this.#geoLightingManager.isEnabled) {
			this.emit('geo_lighting:changed', this.#geoLightingManager.state);
			this.requestRender();
		}
	}

	/**
	 * Set weather preset for lighting conditions
	 *
	 * @param preset - Weather preset name
	 */
	setWeatherPreset (preset: WeatherPreset): void {
		if (!this.#geoLightingManager) return;

		this.#geoLightingManager.setWeather(preset);
		if (this.#geoLightingManager.isEnabled) {
			this.emit('geo_lighting:changed', this.#geoLightingManager.state);
			this.requestRender();
		}
	}

	/**
	 * Update scene center for geographic lighting
	 * Call this after scene composition to properly position sun/moon lights
	 */
	updateGeoLightingCenter (): void {
		const lastBounds = this.#cameraDelegate?.lastSceneBounds;
		if (!this.#geoLightingManager || !lastBounds) return;

		const { min, max } = lastBounds;
		const center = new Vector3(
			(min.x + max.x) / 2,
			(min.y + max.y) / 2,
			(min.z + max.z) / 2
		);

		this.#geoLightingManager.setSceneCenter(center);
	}

	// ========== Readiness ==========

	/**
	 * Resolves when the scene matches its build manifest (all appliances present, countertops
	 * applied, sky texture set). Rejects with SceneNotReadyError after timeoutMs.
	 */
	async waitForSceneReady (options?: { timeoutMs?: number }): Promise<void> {
		return waitForSceneReadyCfn(this, { timeoutMs: options?.timeoutMs ?? 60000 });
	}

	// ========== Export ==========

	/**
	 * Render current scene to image data URL
	 */
	getImageRender (options: ImageRenderOptions = {}): string | null {
		return this.#exportManager?.getImageRender(options) ?? null;
	}

	/**
	 * Get image render with callback (legacy compatibility)
	 */
	getImageRenderWithCallback (pixelRatio: number, callback: (imageData: string) => void): void {
		this.#exportManager?.getImageRenderWithCallback(pixelRatio, callback);
	}

	/**
	 * Export scene as GLB/GLTF model
	 */
	async exportModel (options: ModelExportOptions = {}): Promise<ArrayBuffer | null> {
		if (options.skipDownload) {
			await this.waitForSceneReady({ timeoutMs: options.readinessTimeoutMs ?? 60000 });
		}
		if (!this.#exportManager) throw new ExportSceneUnavailableError('Export manager not initialized');
		const getManifest = () => {
			const builder = this.getBuilderAccess('kitchen');
			return (builder instanceof KitchenSceneBuilder ? builder.currentManifestAccess : null) ?? null;
		};
		try {
			return await this.#exportManager.exportGLB({ ...options, _manifest: getManifest() });
		} catch (err) {
			if (err instanceof ExportValidationError && options.skipDownload) {
				console.warn('[exportModel] GLB validation failed, re-waiting and retrying once:', err.missing);
				await this.waitForSceneReady({ timeoutMs: options.readinessTimeoutMs ?? 60000 });
				return this.#exportManager.exportGLB({ ...options, _manifest: getManifest() });
			}
			throw err;
		}
	}

	/**
	 * Activate AR: iOS AR Quick Look (USDZ), or the `<model-viewer>` WebXR
	 * launch sheet on Android and other WebXR-capable browsers.
	 */
	async activateAR (options: ARExportOptions = {}): Promise<void> {
		if (!this.#exportManager) {
			throw new Error('Export manager not initialized');
		}
		await this.#exportManager.activateAR(options);
	}

	/**
	 * Check if AR is available
	 */
	async isARAvailable (): Promise<boolean> {
		return this.#exportManager?.isARAvailable() ?? false;
	}

	/**
	 * Warm the lazy model-viewer chunk used by the WebXR launch sheet.
	 * Cheap to call once the AR button is visible; no-op on iOS.
	 */
	prewarmAR (): void {
		this.#exportManager?.prewarmAR();
	}

	/**
	 * Register AR session callbacks. The WebXR launch sheet forwards
	 * model-viewer's session status and tracking events through them.
	 */
	setARCallbacks (callbacks: ARCallbacks): void {
		this.#exportManager?.setARCallbacks(callbacks);
	}

	/**
	 * Dismiss the WebXR launch sheet, ending any AR session it started.
	 */
	async stopAR (): Promise<void> {
		await this.#exportManager?.stopAR();
	}

	// ========== Quality ==========

	/**
	 * Set render quality
	 */
	setQuality (quality: QualityLevel): void {
		this.#qualitySettings = getQualitySettings(quality);
		this.#debugMode = quality < 0 || quality === 0;

		this.#sceneManager?.setQuality(quality);

		// Update model manager wireframe mode
		if (this.#modelManager && this.#qualitySettings) {
			this.#modelManager.setWireframe(this.#qualitySettings.wireframe);
		}

		if (this.#cameraManager) {
			this.#cameraManager.debugMode = this.#debugMode;
		}

		if (this.#eventManager) {
			this.#eventManager.debugMode = this.#debugMode;
		}

		if (this.#debugManager) {
			this.#debugManager.setVisible(this.#debugMode);
		}

		// Trigger sky texture upgrade if quality increased and higher resolution available
		this.updateSkyTextureForQuality(quality);

		this.emit('quality:changed', { quality, debugMode: this.#debugMode });

		// Ensure at least one frame renders with the new quality settings
		this.requestRender();
	}

	/**
	 * Update sky texture to match the new quality level.
	 * If a higher resolution texture is available for the current sky,
	 * it will be loaded asynchronously and faded in.
	 */
	private async updateSkyTextureForQuality (quality: number): Promise<void> {
		await this.#textureDelegate?.updateSkyTextureForQuality(quality);
	}

	/**
	 * Set texture quality independently of render quality. Forwards to
	 * SceneTextureDelegate (handles sky swap/clear with fade) and updates
	 * TextureManager config so subsequently loaded textures pick up the new
	 * anisotropy tier. No scene rebuild. Returns a promise that resolves after
	 * the sky upgrade (if any) finishes, so callers can drive a loading state.
	 */
	async setTextureQuality (textureQuality: number | null): Promise<void> {
		this.#config = { ...this.#config, textureQuality };
		this.#textureManager?.updateConfig({ textureQuality });
		// Keep scene builders in sync so any future (per-island) rebuild selects the
		// right sky resolution tier.
		for (const builder of this.#builders.values()) {
			const maybeUpdate = (builder as { updateConfig?: (c: { textureQuality: number | null }) => void }).updateConfig;
			if (typeof maybeUpdate === 'function') {
				maybeUpdate.call(builder, { textureQuality });
			}
		}
		await this.#textureDelegate?.setTextureQuality(textureQuality);
	}

	/**
	 * Set debug mode with optional per-feature options
	 */
	setDebugMode (enabled: boolean, options?: { cutouts?: boolean; lights?: boolean; camera?: boolean; bounds?: boolean }): void {
		this.#debugMode = enabled;
		this.#debugDelegate?.setDebugMode(enabled, options);
	}

	/**
	 * Toggle debug visualization of cutouts specifically
	 * Shows semi-transparent red boxes where cutouts are being applied
	 */
	setDebugCutouts (enabled: boolean): void {
		this.#debugDelegate?.setDebugCutouts(enabled);
	}

	/**
	 * Get debug cutouts state
	 */
	get debugCutoutsEnabled (): boolean {
		return this.#debugDelegate?.debugCutoutsEnabled ?? false;
	}

	/**
	 * Toggle debug visualization of light helpers specifically
	 */
	setDebugLights (enabled: boolean): void {
		this.#debugDelegate?.setDebugLights(enabled);
	}

	/**
	 * Toggle debug visualization of camera helpers specifically
	 */
	setDebugCamera (enabled: boolean): void {
		this.#debugDelegate?.setDebugCamera(enabled);
	}

	/**
	 * Apply custom transition timing from controller options.
	 * Merges the provided timing into the current transition registry.
	 * @param timing - Per-type timing configuration
	 */
	applyTransitionTiming (timing: {
		panel?: { initialize?: number; add?: number; remove?: number; move?: number; rotate?: number };
		countertop?: { initialize?: number; add?: number; remove?: number; move?: number; rotate?: number };
		corner?: { initialize?: number; add?: number; remove?: number; move?: number; rotate?: number };
		trim?: { initialize?: number; add?: number; remove?: number; move?: number; rotate?: number };
		appliance?: { initialize?: number; add?: number; remove?: number; move?: number; rotate?: number };
		cutout?: { initialize?: number; add?: number; remove?: number; move?: number; rotate?: number };
		island?: { initialize?: number; add?: number; remove?: number; move?: number; rotate?: number };
		floor?: { initialize?: number; add?: number; remove?: number; move?: number; rotate?: number };
	}): void {
		if (!this.#sceneTransitionManager) return;

		// Get the current registry
		const registry = this.#sceneTransitionManager.getTransitionRegistry();

		// Merge timing into the registry
		for (const [typeKey, ops] of Object.entries(timing)) {
			const type = typeKey as keyof typeof timing;
			if (!registry[type]) continue;

			for (const [opKey, duration] of Object.entries(ops || {})) {
				const op = opKey as 'initialize' | 'add' | 'remove' | 'move' | 'rotate';
				if (registry[type]?.[op] && typeof duration === 'number') {
					registry[type]![op]!.duration = duration;
				}
			}
		}

		this.#sceneTransitionManager.setTransitionRegistry(registry);
	}

	/**
	 * Toggle visibility of scene objects by their userData.type with fade transitions.
	 * This avoids a full scene rebuild when toggling layer options.
	 * @param type - The userData.type to match (e.g. 'appliance', 'countertop', 'corner', 'debug')
	 * @param visible - Whether the matching objects should be visible
	 */
	setLayerVisibility (type: string, visible: boolean): void {
		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder) return;

		setLayerVisibilityCfn({ type, visible, objectHolder, animationManager: this.#animationManager });
		this.requestRender();
	}

	/**
	 * Create debug visualization for the scene (circles, spheres, etc.)
	 * Called when debug mode is enabled
	 */
	private createDebugSceneVisualization (): void {
		this.#debugDelegate?.createDebugSceneVisualization();
	}

	/**
	 * Set kitchen-specific debug visualization data
	 * Creates comprehensive debug visualizations including:
	 * - Green circle (kitchen bounds) with green center sphere
	 * - Yellow circle (highlight path)
	 * - Light green spheres (left/right X edges)
	 * - Dark green spheres (left/right Z edges)
	 * - Blue sphere (default island X position)
	 * - Red sphere (camera target / optimal viewing angle)
	 * - Cyan sphere (optimal camera position at 5'8")
	 * - Red line (camera to target)
	 * - Orange wireframe (full kitchen bounds)
	 * - Light blue wireframe (left kitchen section)
	 * - Dark blue wireframe (right kitchen section)
	 * - Blue pivot sphere (kitchen origin)
	 */
	setKitchenDebugData (data: KitchenDebugData): void {
		this.#debugDelegate?.setKitchenDebugData(data);
	}

	/**
	 * Create face debug visualization for scene objects (surrounds, countertops, corners, appliances)
	 * Shows 2" spheres at face centers with 18" perpendicular lines
	 */
	async createSceneObjectFaceDebug (): Promise<void> {
		return this.#debugDelegate?.createSceneObjectFaceDebug();
	}

	/**
	 * Create rotation axis arc visualizations for all appliances in the scene that have
	 * non-zero modelRotation values. Intended for use by the data-manager appliance preview
	 * after a scene build completes — not tied to debug mode.
	 * X -> red arc (YZ plane), Y -> green arc (XZ plane), Z -> blue arc (XY plane).
	 */
	createRotationAxisArcs (): void {
		this.#debugDelegate?.createRotationAxisArcs();
	}


	// ========== Events ==========

	/**
	 * Subscribe to an engine event
	 */
	on (event: string, callback: EngineEventCallback): void {
		if (!this.#eventListeners.has(event)) {
			this.#eventListeners.set(event, new Set());
		}
		this.#eventListeners.get(event)!.add(callback);
	}

	/**
	 * Unsubscribe from an engine event
	 */
	off (event: string, callback: EngineEventCallback): void {
		this.#eventListeners.get(event)?.delete(callback);
	}

	/**
	 * Emit an engine event
	 */
	private emit (type: string, data: unknown): void {
		const event: EngineEvent = { type: type as any, data };
		this.#eventListeners.get(type)?.forEach((callback) => callback(event));
	}

	// ========== Rotation Gizmo ==========

	/**
	 * Activate the rotation gizmo on an appliance by its userData.id.
	 * @param objectId The scene object ID to attach the gizmo to
	 */
	activateRotationGizmo (objectId: string): void {
		this.#rotationGizmoManager?.activate(objectId);
	}

	/**
	 * Deactivate the rotation gizmo, removing rings from the scene.
	 */
	deactivateRotationGizmo (): void {
		this.#rotationGizmoManager?.deactivate();
	}

	// ========== Appliance drag ==========

	get applianceDragManager (): ApplianceDragManager | null {
		return this.#applianceDragManager;
	}

	get isApplianceDragActive (): boolean {
		return this.#applianceDragManager?.isActive ?? false;
	}

	/** The domain side of a drag (layout model, planner, commit) — implemented by the controller. */
	setApplianceDragProvider (provider: ApplianceDragProvider | null): void {
		this.#applianceDragManager?.setProvider(provider);
	}

	setApplianceDragEnabled (enabled: boolean, options?: ApplianceDragOptions): void {
		this.#applianceDragManager?.setEnabled(enabled, options);
	}

	cancelApplianceDrag (): void {
		void this.#applianceDragManager?.cancel();
	}

	// ========== Lifecycle ==========

	/**
	 * Request a single re-render on the next animation frame.
	 * Multiple calls within the same frame are coalesced into one render.
	 * If the continuous loop is already running this is a no-op.
	 *
	 * Use this after any state change that requires a visual update
	 * (texture swap, object add/remove, highlight toggle, etc.)
	 */
	requestRender (): void {
		// Flag shadow maps for a single recompute on the next render.
		// Shadow maps only need updating when the scene changes (objects
		// added/removed, materials swapped, lights moved) -- not during
		// camera orbit.  Three.js clears the flag after one render pass.
		const renderer = this.#sceneManager?.renderer;
		if (renderer) renderer.shadowMap.needsUpdate = true;

		this.#sceneManager?.requestRender();
	}

	/**
	 * Called each frame.
	 * Records whether camera-controls moved this frame so the
	 * shouldContinue callback can keep the loop alive while damping.
	 */
	private onFrame (delta: number): void {
		// Update animation manager FIRST so camera position is current for this frame
		this.#animationManager?.update(performance.now());

		// Then update camera controls to process the new position.
		// update() returns true when the camera moved this frame.
		this.#cameraHasUpdated = this.#cameraManager?.update(delta) ?? false;

		// Update helper managers
		this.#lightHelperManager?.update();
		this.#cameraHelperManager?.update();

		// Keep brightness control ring facing the camera
		this.#brightnessControl?.update();

		// Update debug tooltip positions
		this.#debugManager?.updateTooltipPositions();
	}

	/**
	 * Dispose of all resources
	 */
	dispose (): void {
		// Guard against double disposal
		if (this.#isDisposed) {
			return;
		}
		this.#isDisposed = true;

		// Reject any in-flight waitForSceneReady before tearing down — a disposed
		// engine can never become ready, so waiters must fail fast (not poll a corpse).
		this.#lastBuildError = 'engine disposed';
		this.emit('render:error', { error: 'engine disposed' });

		// Stop rendering
		this.#sceneManager?.stopRenderLoop();

		// Cancel any running transitions
		this.#sceneTransitionManager?.cancelTransition();

		// Dispose builders
		for (const builder of this.#builders.values()) {
			builder.dispose?.();
		}
		this.#builders.clear();

		// Dispose managers
		this.#rotationGizmoManager?.dispose();
		this.#exportManager?.dispose();
		this.#cutoutManager?.dispose();
		this.#debugManager?.dispose();
		this.#applianceDragManager?.dispose();
		this.#applianceDragManager = null;
		this.#eventManager?.dispose();
		this.#modelManager?.dispose();
		this.#textureManager?.dispose();
		this.#geoLightingManager?.dispose();
		this.#lightingManager?.dispose();
		this.#cameraManager?.dispose();

		// Dispose helper managers
		this.#brightnessControl?.dispose();
		this.#brightnessControl = null;
		this.#highlightManager?.dispose();
		this.#lightHelperManager?.dispose();
		this.#cameraHelperManager?.dispose();

		// Clear incremental update managers
		this.#sceneDiffManager?.clear();
		this.#sceneDiffManager = null;
		this.#sceneTransitionManager = null;
		this.#hasInitialScene = false;

		// Dispose scene manager last
		this.#sceneManager?.dispose();

		// Clear event listeners
		this.#eventListeners.clear();

		this.#isInitialized = false;
	}

	/**
	 * Deactivate the engine and stop rendering.
	 * Call this when the React component unmounts to free GPU resources.
	 * Alias for dispose() to match Haxe API.
	 */
	deactivate (): void {
		this.dispose();
	}


	/**
	 * Debug helper: Check visibility state of objects in the scene after animations complete.
	 */
	private debugCheckObjectOpacity (container: Object3D): void {
		this.#debugDelegate?.debugCheckObjectOpacity(container);
	}

}


